import { ref, computed, reactive } from 'vue';
import {
  createPlatformData,
  repairMissingMaterialPreviews,
  ROLE_NAV,
  ROLE_PERMS,
  resolveMergedRoleNav,
  resolveMergedRolePerms,
} from '@/data/platformData';
import {
  getRoleKey,
  getPrintConfig,
  genOrderId,
  resolveCdpAsset,
  isCdpAssetPath,
  cdpUrl,
  dataUrlToFile,
  DEFAULT_TEMPLATE_THUMB,
} from '@/utils/platformHelpers';
import { extractPdfFirstPageDataUrl, resolvePdfCoverForImport } from '@/utils/pdfThumbnail';
import { assertPdfPhysicalPagesAllowed } from '@/utils/templateImportLimits';
import { isUsableTemplateCover } from '@/utils/templateThumb';
import {
  pushRoute,
  getAppRouter,
  getEditorReturnRoute,
  setEditorReturnRoute,
  clearEditorReturnRoute,
} from '@/services/navigation';
import { fontService } from '@/services/apiClient';
import { mapFontDtoToPlatform, mapPlatformFontToUploadPayload } from '@/utils/fontMapper';
import {
  unwrapApiResult,
  normalizeMaterialFolderId,
  materialFolderIdsEqual,
  normalizeTemplateFolderId,
  normalizeTemplateLayerJson,
  toFullEditorLayerJson,
  isSpawnedTemplateCopyName,
} from '@/utils/artMapper';
import { ROLE_CHINESE_NAMES, mapJwtPayloadToAuthSession } from '@/utils/authMapper';
import { setAuthToken, clearAuthToken, getAuthToken, clearLogoutFlag } from '@/utils/authToken';
import { createArtApiSync, getViewModuleKey, USE_ART_API } from '@/composables/useArtApiSync';
import { buildEditorSyncPayload, notifyEditorDataSync } from '@/utils/editorBridge';
import { executeEditorApiAction } from '@/services/editorApiBridge';
import { syncOrganizationStores } from '@/services/organizationStore';
import {
  syncMaterialLibrary,
  createMaterialFolderRemote,
  renameMaterialFolderRemote,
  deleteMaterialFolderRemote,
  moveMaterialToFolderRemote,
  batchMoveMaterialsRemote,
  uploadMaterialRemote,
  renameMaterialRemote,
  deleteMaterialRemote,
  batchDeleteMaterialsRemote,
} from '@/services/materialStore';
import {
  syncTemplateLibrary,
  createTemplateFolderRemote,
  renameTemplateFolderRemote,
  deleteTemplateFolderRemote,
  moveTemplateToFolderRemote,
  batchMoveTemplatesRemote,
  importTemplateRemote,
  importTemplateWithLayerStorageRemote,
  assemblePdfTemplateImportFields,
  updateTemplateContentRemote,
  isRemoteTemplateId,
  renameTemplateRemote,
  deleteTemplateRemote,
  fetchTemplatePageLayers,
  mergeTemplatePageLayers,
  resolveRemoteTemplateForUse,
  hasUsableTemplateLayerJson,
} from '@/services/templateStore';
import {
  syncQueryMaterialLibrary,
  syncQueryTemplateLibrary,
} from '@/services/queryLibraryStore';
import { resolveTemplateImportLayers } from '@/services/templateLayerImport';
import { CDP_ENDPOINTS, fetchCdpEngineJson } from '@/services/cdpEngineClient';
import {
  beginPdfParseSession,
  cancelPdfParsePolling,
  countLayerJsonPages,
  importPdfTemplateLayersFromOss,
} from '@/services/pdfParseTaskStore';
import {
  uploadFontFileToOss,
  uploadMaterialFileToOss,
  uploadTemplateFileToOss,
} from '@/services/attachmentStore';
import {
  resolveShopId,
  toPlatformStoreId,
  applySessionDepartment,
  restoreSessionDepartment,
  resolvePlatformStorageKey,
  isAllPlatformStore,
  resolveStoreRecord,
  resolveUserId,
} from '@/utils/materialApiContext';

import {
  syncPaletteLibrary,
  savePaletteColorRemote,
  deletePaletteColorRemote,
  resolvePaletteStoreKey,
  fetchPaletteScope,
} from '@/services/paletteStore';
import {
  createDesignCenterRemote,
  deleteDesignCenterRemote,
  updateDesignCenterRemote,
  saveMasterTemplateRemote,
  getDesignCenterDetailRemote,
  DESIGN_CENTER_TEMPLATE_TYPE,
  DESIGN_CENTER_OP_TYPE,
  DESIGN_CENTER_LIST_OP_TYPE,
  buildDesignCenterSearchParams,
} from '@/services/designCenterStore';
import { savePrintConfigRemote } from '@/services/configStore';
import {
  loadPendingTemplates,
  mergeRemoteTemplatesWithPending,
  removePendingTemplate,
  savePendingTemplate,
} from '@/services/templatePendingStore';
import { setHttpErrorNotifier } from '@/services/httpErrorHandler';
import { normalizeTaskSkus } from '@/services/designerTaskService';
import { hasFullStoreAccessRole } from '@/utils/orgMapper';
import { canDeleteMaterial as canDeleteMaterialByPolicy, hasDeleteAnyMaterialAccess } from '@/utils/materialPermissions';
import { isUnitTestRuntime } from '@/utils/runtimeEnv';

const USE_REMOTE_MATERIALS = USE_ART_API && !isUnitTestRuntime();
const USE_REMOTE_TEMPLATES = USE_ART_API && !isUnitTestRuntime();
const USE_REMOTE_PALETTES = USE_ART_API && !isUnitTestRuntime();
const USE_REMOTE_DESIGN_CENTER = USE_ART_API && !isUnitTestRuntime();
const USE_REMOTE_CONFIG = USE_ART_API && !isUnitTestRuntime();

const STORAGE_KEY = 'cloud_design_platform';
const FONT_STORAGE_KEY = 'cloud_design_platform_fonts';
const TEMPLATE_CACHE_MIGRATION_KEY = 'jx_template_api_migration_v2';
const USE_FONT_API = import.meta.env.VITE_USE_FONT_API !== 'false';
const persistTemplateInFlight = new Map();
const saveMasterTemplateInFlight = new Map();

const DEFAULT_SYSTEM_FONTS = [
  { id: 'sys_yahei', name: '微软雅黑', family: 'Microsoft YaHei', fileName: 'Microsoft YaHei', source: 'system', uploadedAt: '系统预设' },
  { id: 'sys_simsun', name: '宋体', family: 'SimSun', fileName: 'SimSun', source: 'system', uploadedAt: '系统预设' },
  { id: 'sys_arial', name: 'Arial', family: 'Arial', fileName: 'Arial', source: 'system', uploadedAt: '系统预设' },
  { id: 'sys_helvetica', name: 'Helvetica', family: 'Helvetica', fileName: 'Helvetica', source: 'system', uploadedAt: '系统预设' },
  { id: 'sys_noto', name: 'Noto Sans SC', family: 'Noto Sans SC', fileName: 'Noto Sans SC', source: 'system', uploadedAt: '系统预设' },
];

const platform = reactive(createPlatformData());
platform.queryTemplates = [];
platform.queryTemplateFolders = {};
platform.queryTemplateTotalCount = {};
platform.queryMaterials = [];
platform.queryMaterialFolders = {};
platform.queryMaterialTotalCount = {};
platform.designCenterCounts = { saved: null, drafts: null };
platform.designCenterSearchTotals = { saved: null, drafts: null };
const ROLE_LABELS = {
  platform_admin: '平台管理员',
  store_manager: '店长',
  designer: '设计师',
  customer_service: '客服',
};

const DEFAULT_ROLE_STORES = {
  platform_admin: [],
  admin: [],
  store_manager: [],
  manager: [],
  designer: [],
  customer_service: [],
  service: [],
};

function getOrCreateUserProfile(roleKey = 'designer') {
  const normalizedKey = getRoleKey(roleKey) || 'designer';
  if (!platform.users[roleKey]) {
    const rawUser = typeof localStorage !== 'undefined' ? (function() {
      try { return JSON.parse(localStorage.getItem('cdp_user') || 'null'); } catch { return null; }
    })() : null;

    const primaryRole = rawUser?.primaryRole || rawUser?.roles?.[0] || session.backendRoles?.[0] || '';
    const roleTitle = rawUser?.roleTitle || ROLE_CHINESE_NAMES[primaryRole] || ROLE_LABELS[normalizedKey] || '平台管理员';
    const userName = rawUser?.userName || session.userName || ROLE_LABELS[normalizedKey] || '用户';

    platform.users[roleKey] = {
      id: String(rawUser?.userId || session.userId || '1549'),
      name: userName,
      role: normalizedKey,
      roleLabel: roleTitle,
      stores: Array.isArray(rawUser?.stores) ? rawUser.stores : (DEFAULT_ROLE_STORES[roleKey] || DEFAULT_ROLE_STORES[normalizedKey] || []),
      avatar: (userName || roleTitle || '设')[0],
      weChatWorkAccount: rawUser?.weChatWorkAccount || session.weChatWorkAccount || '',
      backendRoles: rawUser?.roles || session.backendRoles || [],
    };
  }
  return platform.users[roleKey];
}

const savedRole = typeof localStorage !== 'undefined' ? localStorage.getItem('cdp_role') : '';
const savedStoreId = typeof localStorage !== 'undefined' ? localStorage.getItem('cdp_store') : '';
const savedDeptId = typeof localStorage !== 'undefined' ? localStorage.getItem('cdp_department_id') : '';
const savedDeptName = typeof localStorage !== 'undefined' ? localStorage.getItem('cdp_department_name') : '';
const savedUser = typeof localStorage !== 'undefined' ? (function() {
  try {
    return JSON.parse(localStorage.getItem('cdp_user') || 'null');
  } catch { return null; }
})() : null;

const session = reactive({
  role: savedRole || 'designer',
  storeId: savedStoreId || 'all',
  departmentId: (savedDeptId && Number(savedDeptId) > 0) ? Number(savedDeptId) : null,
  departmentName: savedDeptName || '',
  userName: savedUser?.userName || '',
  userId: savedUser?.userId || null,
  weChatWorkAccount: savedUser?.weChatWorkAccount || '',
  backendRoles: savedUser?.roles || [],
});

if (savedUser && platform.users[session.role]) {
  const profile = platform.users[session.role];
  if (savedUser.userName) {
    profile.name = savedUser.userName;
    profile.avatar = String(savedUser.userName).slice(0, 1) || profile.avatar;
  }
  if (savedUser.roleTitle) profile.roleLabel = savedUser.roleTitle;
  if (savedUser.userId != null) profile.id = String(savedUser.userId);
  if (savedUser.weChatWorkAccount) profile.weChatWorkAccount = savedUser.weChatWorkAccount;
  if (Array.isArray(savedUser.roles)) profile.backendRoles = [...savedUser.roles];
}

if (typeof localStorage !== 'undefined') {
  applySessionDepartment(session, session.storeId, platform.stores, localStorage);
  restoreSessionDepartment(session, platform.stores, localStorage);
}
function isQueryTemplateView(view) {
  return view === 'templates' || view === 'templates1';
}
function isQueryMaterialView(view) {
  return view === 'materials' || view === 'materials1';
}

const currentView = ref('templates');
const drafts = ref([]);
const ACTIVE_EDITOR_META_STORAGE_KEY = 'jx_active_editor_meta';

function editorRouteQueryValue(query, ...keys) {
  for (const key of keys) {
    const value = query instanceof URLSearchParams ? query.get(key) : query?.[key];
    const normalized = Array.isArray(value) ? value[0] : value;
    if (normalized != null && String(normalized).trim() !== '') return String(normalized);
  }
  return '';
}

export function mergeEditorMetaWithRouteQuery(meta, query = {}) {
  const urlTpl = editorRouteQueryValue(query, 'tpl', 'templateId');
  if (!urlTpl) return meta;
  const urlDeptId = editorRouteQueryValue(query, 'dept', 'departmentId');
  const urlDeptName = editorRouteQueryValue(query, 'deptName', 'departmentName');
  const urlStoreId = editorRouteQueryValue(query, 'store', 'storeId');
  const urlTitle = editorRouteQueryValue(query, 'title', 'name');
  const persistedTemplateId = meta?.templateId || meta?.fromTemplate;
  const isSameTemplate = String(persistedTemplateId || '') === String(urlTpl);
  const reusableMeta = isSameTemplate
    ? (meta || {})
    : {
        returnView: meta?.returnView,
        returnRoute: meta?.returnRoute,
      };
  return {
    ...reusableMeta,
    id: isSameTemplate && meta?.id ? meta.id : `d_url_${urlTpl}`,
    templateId: urlTpl,
    fromTemplate: urlTpl,
    templateName: urlTitle || (isSameTemplate ? meta?.templateName : '') || '',
    fromTemplateLibrary: true,
    standalone: true,
    storeId: urlStoreId || (isSameTemplate ? meta?.storeId : '') || 's1',
    departmentId: urlDeptId ? Number(urlDeptId) : (isSameTemplate ? (meta?.departmentId ?? null) : null),
    departmentName: urlDeptName || (isSameTemplate ? meta?.departmentName : '') || '',
    editMasterTemplate: editorRouteQueryValue(query, 'editMaster') === '1'
      || Boolean(isSameTemplate && meta?.editMasterTemplate),
  };
}

function slimMetaForPersistence(meta) {
  if (!meta) return null;
  const { layerJson, ...rest } = meta;
  if (typeof layerJson === 'string' && layerJson.length <= 50_000) {
    return { ...rest, layerJson };
  }
  return rest;
}

function loadPersistedEditorMeta() {
  let meta = null;
  if (typeof localStorage !== 'undefined') {
    try {
      const raw = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(ACTIVE_EDITOR_META_STORAGE_KEY))
        || localStorage.getItem(ACTIVE_EDITOR_META_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          meta = parsed;
          if (!meta.layerJson) {
            const candidateIds = [meta.templateId, meta.fromTemplate, meta.id].filter(Boolean);
            for (const id of candidateIds) {
              const cached = localStorage.getItem(`jx_editor_layer_json_${id}`)
                || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(`jx_editor_layer_json_${id}`));
              if (cached) {
                meta.layerJson = cached;
                break;
              }
            }
          }
        }
      }
    } catch { /* ignore */ }
  }

  // 页面刷新时从当前 URL Query 参数（如 /editor?tpl=80&dept=27）作为高优先级补全
  if (typeof window !== 'undefined' && window.location) {
    try {
      const search = window.location.search || (window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '');
      const params = new URLSearchParams(search);
      meta = mergeEditorMetaWithRouteQuery(meta, params);
      if (meta && !meta.layerJson) {
        const candidateIds = [meta.templateId, meta.fromTemplate, meta.id].filter(Boolean);
        for (const id of candidateIds) {
          const cached = localStorage.getItem(`jx_editor_layer_json_${id}`)
            || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(`jx_editor_layer_json_${id}`));
          if (cached) {
            meta.layerJson = cached;
            break;
          }
        }
      }
    } catch { /* ignore */ }
  }
  return meta;
}

function persistActiveEditorMeta(meta) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (!meta) {
      sessionStorage.removeItem(ACTIVE_EDITOR_META_STORAGE_KEY);
      localStorage.removeItem(ACTIVE_EDITOR_META_STORAGE_KEY);
    } else {
      const slim = slimMetaForPersistence(meta);
      const raw = JSON.stringify(slim);
      sessionStorage.setItem(ACTIVE_EDITOR_META_STORAGE_KEY, raw);
      localStorage.setItem(ACTIVE_EDITOR_META_STORAGE_KEY, raw);
      if (meta.layerJson && typeof meta.layerJson === 'string') {
        const candidateIds = [meta.templateId, meta.fromTemplate, meta.id].filter(Boolean);
        candidateIds.forEach((id) => {
          try {
            localStorage.setItem(`jx_editor_layer_json_${id}`, meta.layerJson);
          } catch { /* ignore */ }
        });
      }
    }
  } catch {
    try {
      const minimal = {
        id: meta.id,
        templateId: meta.templateId || meta.fromTemplate,
        fromTemplate: meta.fromTemplate || meta.templateId,
        templateName: meta.templateName,
        departmentId: meta.departmentId,
        departmentName: meta.departmentName,
        platformSessionStoreId: meta.platformSessionStoreId,
        storeId: meta.storeId,
        returnView: meta.returnView,
        fromTemplateLibrary: meta.fromTemplateLibrary,
        standalone: meta.standalone,
        isNew: meta.isNew,
        orderId: meta.orderId,
      };
      const rawMin = JSON.stringify(minimal);
      sessionStorage.setItem(ACTIVE_EDITOR_META_STORAGE_KEY, rawMin);
      localStorage.setItem(ACTIVE_EDITOR_META_STORAGE_KEY, rawMin);
      if (meta.layerJson && typeof meta.layerJson === 'string') {
        const candidateIds = [meta.templateId, meta.fromTemplate, meta.id].filter(Boolean);
        candidateIds.forEach((id) => {
          try {
            localStorage.setItem(`jx_editor_layer_json_${id}`, meta.layerJson);
          } catch { /* ignore */ }
        });
      }
    } catch { /* ignore quota */ }
  }
}

function syncEditorMetaFromRouteQuery(query = {}) {
  const next = mergeEditorMetaWithRouteQuery(editorMeta.value, query);
  if (!next || next === editorMeta.value) return editorMeta.value;
  editorMeta.value = next;
  persistActiveEditorMeta(next);
  pushEditorSync();
  return next;
}

function getInitialEditorReturnView() {
  const meta = loadPersistedEditorMeta();
  if (meta?.returnView) return meta.returnView;
  const route = getEditorReturnRoute();
  if (route) {
    const match = route.match(/\/platform\/([^/?#]+)/);
    if (match && match[1]) return match[1];
  }
  return 'templates';
}

const editorDraftCache = ref([]);
const toast = ref({ show: false, message: '', type: '' });
const editorMeta = ref(loadPersistedEditorMeta());
const editorReturnView = ref(getInitialEditorReturnView());
const platformFonts = ref([...DEFAULT_SYSTEM_FONTS]);
const templateImportState = ref({ active: false, message: '' });
const openingDesignId = ref('');

let toastTimer = null;

function showToast(message, type = '') {
  toast.value = { show: true, message, type };
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.value.show = false; }, 2800);
}

if (!isUnitTestRuntime()) {
  setHttpErrorNotifier((message) => showToast(message, 'warn'));
}

let fontLibraryUnsubscribe = null;
let fontLoadPromise = null;
let fontsLoadedAt = 0;
let fontRemoteRetryAt = 0;
const fontApiAvailable = ref(USE_FONT_API);
const registeredPlatformFontFaces = new Map();
const FONT_CACHE_TTL_MS = 30_000;

function normalizeRemoteFontList(payload) {
  const result = unwrapApiResult(payload);
  const list = Array.isArray(result)
    ? result
    : (Array.isArray(result?.records) ? result.records : (result?.list || []));
  return list.map(mapFontDtoToPlatform);
}

async function fetchRemoteFonts() {
  const payload = await fontService.getAll();
  return normalizeRemoteFontList(payload);
}

async function loadLocalUploadedFonts() {
  if (window.CdpFontLibrary?.list) {
    return window.CdpFontLibrary.list();
  }
  const raw = localStorage.getItem(FONT_STORAGE_KEY) || localStorage.getItem('cdp_font_library');
  const parsed = raw ? JSON.parse(raw) : [];
  const list = Array.isArray(parsed) ? parsed : (parsed.fonts || []);
  return list.filter(font => font && font.source !== 'system');
}

const CORE_CDP_INSTALLED_FONTS = ['Microsoft YaHei', 'SimSun', 'Arial', 'Helvetica', 'Noto Sans SC'];

function normalizeFontKey(name) {
  return String(name || '').replace(/["'\s._-]/g, '').toLowerCase();
}

function syncFontsToCdpStorage() {
  const names = platformFonts.value.flatMap(f => [f.family, f.name, f.postScriptName].filter(Boolean));
  const metadata = platformFonts.value.map(font =>
    window.CdpFontLibrary?.metadataOnly?.(font) || {
      ...font,
      blob: undefined,
      dataUrl: undefined,
    }
  );
  try {
    localStorage.setItem(FONT_STORAGE_KEY, JSON.stringify(metadata));
    localStorage.setItem('cdp_font_library', JSON.stringify({
      version: 2,
      fonts: metadata,
      installedFonts: [...new Set([...CORE_CDP_INSTALLED_FONTS, ...names])],
    }));
  } catch { /* ignore */ }
  pushEditorSync();
}

function pushEditorSync() {
  notifyEditorDataSync(buildEditorSyncPayload({
    materials: platform.materials,
    materialFolders: platform.materialFolders || {},
    platformFonts: platformFonts.value,
    useRemoteMaterials: USE_REMOTE_MATERIALS,
    deletedDefaultMaterialIds: platform.deletedDefaultMaterialIds || [],
    templates: platform.templates,
    templateFolders: platform.templateFolders || {},
    useRemoteTemplates: USE_REMOTE_TEMPLATES,
    useRemoteFonts: USE_FONT_API && fontApiAvailable.value,
    useRemoteDesignCenter: USE_REMOTE_DESIGN_CENTER,
  }));
}

async function registerFontFaces(list) {
  if (typeof FontFace !== 'function' || !document.fonts) return;
  const activeIds = new Set(list.filter(font => font?.source !== 'system').map(font => font.id));
  registeredPlatformFontFaces.forEach((face, id) => {
    if (activeIds.has(id)) return;
    document.fonts.delete(face);
    registeredPlatformFontFaces.delete(id);
  });
  for (const font of list) {
    if (!font?.family || font.source === 'system' || registeredPlatformFontFaces.has(font.id)) continue;
    try {
      let face = null;
      if (font.blob && window.CdpFontLibrary?.loadFace) {
        face = await window.CdpFontLibrary.loadFace(font, document);
      } else if (font.fileUrl) {
        face = new FontFace(font.family, `url(${font.fileUrl})`, {
          weight: String(font.weight || 400),
          style: font.style || 'normal',
          display: 'swap',
        });
        await face.load();
        document.fonts.add(face);
      } else if (font.dataUrl) {
        face = new FontFace(font.family, `url(${font.dataUrl})`, {
          weight: String(font.weight || 400),
          style: font.style || 'normal',
          display: 'swap',
        });
        await face.load();
        document.fonts.add(face);
      }
      if (face) registeredPlatformFontFaces.set(font.id, face);
    } catch { /* ignore */ }
  }
}

async function performFontLoad() {
  try {
    let uploaded = [];
    let remoteSystem = [];

    if (USE_FONT_API && Date.now() >= fontRemoteRetryAt && !isUnitTestRuntime()) {
      try {
        const remote = await fetchRemoteFonts();
        fontApiAvailable.value = true;
        fontRemoteRetryAt = 0;
        remoteSystem = remote.filter(font => font.source === 'system');
        uploaded = remote.filter(font => font.source !== 'system');
      } catch (error) {
        fontApiAvailable.value = false;
        fontRemoteRetryAt = Date.now() + FONT_CACHE_TTL_MS;
        uploaded = await loadLocalUploadedFonts();
        if (import.meta.env.DEV) {
          console.warn('[fonts] 远程字体库不可用，已回退本地存储', error);
        }
      }
    } else {
      uploaded = await loadLocalUploadedFonts();
    }

    platformFonts.value = [
      ...(remoteSystem.length ? remoteSystem : DEFAULT_SYSTEM_FONTS),
      ...uploaded,
    ];
    syncFontsToCdpStorage();
    await registerFontFaces(uploaded);
    return platformFonts.value;
  } catch {
    syncFontsToCdpStorage();
    return platformFonts.value;
  }
}

function loadFonts({ force = false } = {}) {
  if (fontLoadPromise) return fontLoadPromise;
  if (!force && fontsLoadedAt && Date.now() - fontsLoadedAt < FONT_CACHE_TTL_MS) {
    return Promise.resolve(platformFonts.value);
  }
  const run = () => performFontLoad().then((result) => {
    fontsLoadedAt = Date.now();
    return result;
  });
  fontLoadPromise = (artApiSync?.withModuleLoading
    ? artApiSync.withModuleLoading('fonts', run)
    : run()
  ).finally(() => {
    fontLoadPromise = null;
  });
  return fontLoadPromise;
}

function bindFontLibraryBridge() {
  if (fontLibraryUnsubscribe || !window.CdpFontLibrary?.subscribe) return;
  fontLibraryUnsubscribe = window.CdpFontLibrary.subscribe(() => {
    loadFonts({ force: true });
  });
}

function normalizeThumb(thumb) {
  if (!thumb || typeof thumb !== 'string') return thumb;
  if (/^(https?:|data:|blob:)/i.test(thumb)) return thumb;
  return resolveCdpAsset(thumb);
}

function normalizeMaterialUrls() {
  platform.materials.forEach(m => {
    if (m.url && !/^data:/i.test(m.url)) m.url = resolveCdpAsset(m.url);
  });
}

function normalizeTemplateAssetPaths() {
  platform.templates.forEach(t => {
    if (t.thumb) t.thumb = normalizeThumb(t.thumb);
  });
  platform.designs.forEach(d => {
    if (d.thumb) d.thumb = normalizeThumb(d.thumb);
  });
  (platform.standaloneDesigns || []).forEach(d => {
    if (d.thumb) d.thumb = normalizeThumb(d.thumb);
  });
}

function mergeTemplateFromStorage(st) {
  const idx = platform.templates.findIndex(t => t.id === st.id);
  if (idx < 0) {
    platform.templates.unshift({
      ...st,
      thumb: normalizeThumb(st.thumb),
    });
    return;
  }
  const seed = platform.templates[idx];
  const next = { ...seed, ...st };
  // 系统种子模版始终使用仓库中的最新定义；用户自行导入的模版仍可从缓存恢复。
  next.folderId = seed.folderId;
  next.thumb = normalizeThumb(seed.thumb || st.thumb);
  if (!isCdpAssetPath(next.thumb) && isCdpAssetPath(seed.thumb)) {
    next.thumb = seed.thumb;
  }
  if (seed.layerStatus) next.layerStatus = seed.layerStatus;
  if (seed.layered != null) next.layered = seed.layered;
  if (seed.colorManaged != null) next.colorManaged = seed.colorManaged;
  if (seed.jsonSchemaVersion) next.jsonSchemaVersion = seed.jsonSchemaVersion;
  if (seed.tags) next.tags = seed.tags;
  if (seed.name) next.name = seed.name;
  if (seed.layerJson) next.layerJson = seed.layerJson;
  platform.templates[idx] = next;
}

function migrateLegacyTemplateCache() {
  if (!USE_REMOTE_TEMPLATES || typeof localStorage === 'undefined') return;
  if (localStorage.getItem(TEMPLATE_CACHE_MIGRATION_KEY)) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      delete data.templates;
      delete data.templateFolders;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
    localStorage.removeItem('cdp_recent_templates');
  } catch { /* ignore */ }
  localStorage.setItem(TEMPLATE_CACHE_MIGRATION_KEY, '1');
}

function isRemoteDesignCenterId(id) {
  if (id == null || id === '') return false;
  const value = String(id);
  if (value.startsWith('sd_') || value.startsWith('d_') || value.startsWith('draft_')) return false;
  return /^\d+$/.test(value);
}

let designCenterSyncTimer = null;

function queueDesignCenterStorageSync() {
  if (!USE_REMOTE_DESIGN_CENTER) return;
  clearTimeout(designCenterSyncTimer);
  designCenterSyncTimer = setTimeout(() => {
    syncDesignCenterFromLocalStorage().catch((error) => {
      if (import.meta.env.DEV) console.warn('[art-api] 设计中心本地同步失败', error);
    });
  }, 500);
}

async function syncDesignCenterFromLocalStorage() {
  if (!USE_REMOTE_DESIGN_CENTER) return;
  const { storeId, user } = getMaterialApiContext();
  const savedLocals = (platform.standaloneDesigns || []).filter(
    d => d?.state && isRemoteDesignCenterId(d.id) && Boolean(d.pendingRemoteSync),
  );
  const draftLocals = (editorDraftCache.value || []).filter(
    d => d?.state && isRemoteDesignCenterId(d.id) && Boolean(d.pendingRemoteSync),
  );
  const savedIds = new Set(savedLocals.map(d => String(d.id)));
  const apiContext = storesApiContext();
  const tasks = [
    ...savedLocals.map(async d => {
      const updated = await updateDesignCenterRemote(storeId, user, {
        ...d,
        templateType: DESIGN_CENTER_TEMPLATE_TYPE.SAVED,
        opType: DESIGN_CENTER_OP_TYPE.SAVE,
      }, platform.stores, apiContext);
      d.pendingRemoteSync = false;
      return updated;
    }),
    ...draftLocals
      .filter(d => !savedIds.has(String(d.id)))
      .map(async d => {
        const updated = await updateDesignCenterRemote(storeId, user, {
          ...d,
          templateType: DESIGN_CENTER_TEMPLATE_TYPE.DRAFT,
          opType: DESIGN_CENTER_OP_TYPE.DRAFT,
        }, platform.stores, apiContext);
        d.pendingRemoteSync = false;
        return updated;
      }),
  ];
  if (!tasks.length) return;
  const results = await Promise.allSettled(tasks);
  results.forEach((result) => {
    if (result.status !== 'fulfilled') return;
    const updated = result.value;
    const idx = drafts.value.findIndex(d => d.id === updated.id);
    if (idx >= 0) drafts.value[idx] = { ...drafts.value[idx], ...updated, pendingRemoteSync: false };
    else drafts.value.unshift({ ...updated, pendingRemoteSync: false });
  });
  saveStorage();
}

function loadStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const data = JSON.parse(raw);
    if (USE_REMOTE_DESIGN_CENTER) {
      editorDraftCache.value = data.drafts || [];
    } else {
      drafts.value = data.drafts || [];
    }
    (data.designs || []).forEach(sd => {
      if (sd.thumb) sd.thumb = normalizeThumb(sd.thumb);
      const idx = platform.designs.findIndex(d => d.id === sd.id);
      if (idx >= 0) platform.designs[idx] = sd;
      else platform.designs.unshift(sd);
    });
    if (!USE_ART_API && data.templates) {
      data.templates.forEach(st => mergeTemplateFromStorage(st));
    }
    if (!USE_ART_API && Array.isArray(data.materials)) {
      platform.materials = data.materials.map(m => ({ ...m }));
    }
    if (!USE_ART_API) {
      platform.deletedDefaultMaterialIds = Array.isArray(data.deletedDefaultMaterialIds)
        ? [...new Set(data.deletedDefaultMaterialIds)]
        : [];
    }
    if (!USE_ART_API && data.materialFolders && typeof data.materialFolders === 'object') {
      const seedFolders = platform.materialFolders || {};
      const storeIds = new Set([...Object.keys(seedFolders), ...Object.keys(data.materialFolders)]);
      platform.materialFolders = Object.fromEntries([...storeIds].map(sid => {
        const merged = new Map();
        (seedFolders[sid] || []).forEach(folder => merged.set(folder.id, { ...folder }));
        (data.materialFolders[sid] || []).forEach(folder => merged.set(folder.id, { ...folder }));
        return [sid, [...merged.values()]];
      }));
    }
    if (!USE_ART_API && data.templateFolders && typeof data.templateFolders === 'object') {
      // 合并文件夹：保留用户新建，同时补上种子里的标准文件夹（粉色底袋/黑色底袋等）
      const seedFolders = platform.templateFolders || {};
      const merged = { ...seedFolders };
      Object.keys(data.templateFolders).forEach(sid => {
        const cached = data.templateFolders[sid] || [];
        const seed = seedFolders[sid] || [];
        const byId = new Map();
        seed.forEach(f => byId.set(f.id, { ...f }));
        cached.forEach(f => {
          if (!byId.has(f.id)) byId.set(f.id, { ...f });
        });
        merged[sid] = [...byId.values()];
      });
      platform.templateFolders = merged;
    }
    if (Array.isArray(data.standaloneDesigns)) {
      platform.standaloneDesigns = data.standaloneDesigns.map(d => ({
        ...d,
        thumb: normalizeThumb(d.thumb),
      }));
    }
    mergeLocalSavedDesignsIntoDrafts();
    mergeLocalDraftsIntoDrafts();
    if (data.printConfig?.stores) platform.printConfig.stores = data.printConfig.stores;
    if (!USE_ART_API && data.commonColors) platform.commonColors = data.commonColors;
    normalizeMaterialUrls();
  } catch { /* ignore */ }
}

function onExternalStorageChange(event) {
  if (event.key !== STORAGE_KEY) return;
  loadStorage();
}

let storageBridgeBound = false;

function bindStorageBridge() {
  if (storageBridgeBound || typeof window === 'undefined') return;
  storageBridgeBound = true;
  window.addEventListener('storage', onExternalStorageChange);
}

function mergeLocalSavedDesignsIntoDrafts() {
  if (!USE_REMOTE_DESIGN_CENTER) return;
  const localSaved = platform.designs.filter(d =>
    !d.parentDesignId && (d.status === 'done' || d.remoteSaved),
  );
  localSaved.forEach((design) => {
    const normalized = {
      ...design,
      templateType: DESIGN_CENTER_TEMPLATE_TYPE.SAVED,
      status: 'done',
    };
    const idx = drafts.value.findIndex(d => String(d.id) === String(design.id));
    if (idx >= 0) drafts.value[idx] = { ...drafts.value[idx], ...normalized };
    else drafts.value.unshift(normalized);
  });
}

function upsertLocalEditorDraft(entry = {}) {
  if (!entry?.id) return;
  const id = String(entry.id);
  const normalized = {
    ...entry,
    id,
    status: 'draft',
    templateType: DESIGN_CENTER_TEMPLATE_TYPE.DRAFT,
    savedAt: entry.savedAt || entry.updated || new Date().toLocaleString('zh-CN', { hour12: false }),
  };
  const cacheIdx = editorDraftCache.value.findIndex(d => String(d.id) === id);
  if (cacheIdx >= 0) {
    editorDraftCache.value[cacheIdx] = { ...editorDraftCache.value[cacheIdx], ...normalized };
  } else {
    editorDraftCache.value = [normalized, ...editorDraftCache.value];
  }
  const draftIdx = drafts.value.findIndex(d => String(d.id) === id);
  if (draftIdx >= 0) {
    drafts.value[draftIdx] = { ...drafts.value[draftIdx], ...normalized };
  } else {
    drafts.value.unshift(normalized);
  }
}

function mergeLocalDraftsIntoDrafts() {
  if (!USE_REMOTE_DESIGN_CENTER) return;
  const savedIds = new Set(
    drafts.value
      .filter(d => d.templateType === DESIGN_CENTER_TEMPLATE_TYPE.SAVED || d.status === 'done')
      .map(d => String(d.id)),
  );
  editorDraftCache.value.forEach((draft) => {
    if (!draft?.id || savedIds.has(String(draft.id))) return;
    if (draft.templateType === DESIGN_CENTER_TEMPLATE_TYPE.SAVED || draft.status === 'done') return;
    upsertLocalEditorDraft(draft);
  });
}

function belongsToCurrentAccount(item, account, accountRoleKey, accountName) {
  if (!item) return false;
  // 远程设计中心列表已由后端按 departmentId 和当前登录态过滤；
  // createUserName 是后端账号姓名，不能与前端演示身份姓名直接比较。
  if (item.remoteDesignCenter) return true;
  if (item.ownerId) return item.ownerId === account;
  if (item.ownerRole) return getRoleKey(item.ownerRole) === accountRoleKey;
  if (item.ownerName && accountName) return item.ownerName === accountName;
  // 远程设计中心通常无 owner 字段，按店铺过滤后默认可见
  if (item.templateType != null || item.draftStatus != null) return true;
  if (item.status === 'done' || item.remoteSaved) return true;
  return false;
}

function currentOwnerMeta() {
  const me = getOrCreateUserProfile(session.role);
  return {
    ownerId: session.role || 'designer',
    ownerRole: me.role || session.role || 'designer',
    ownerName: me.name || '设计员',
  };
}

function omitInlineAsset(item) {
  if (!item) return item;
  const stripHuge = (value, limit = 120000) => {
    if (typeof value === 'string' && value.startsWith('data:') && value.length > limit) return '';
    return value;
  };
  const next = {
    ...item,
    url: stripHuge(item.url, 120000),
    thumb: stripHuge(item.thumb, 180000),
  };
  if (!next.url && next.thumb) next.url = next.thumb;
  if (!next.thumb && next.url && next.url.length <= 180000) next.thumb = next.url;
  return next;
}

function slimMaterialsForQuota(list, mode = 'soft') {
  return (list || []).map(item => {
    if (!item) return item;
    const next = { ...item };
    const url = typeof next.url === 'string' ? next.url : '';
    const thumb = typeof next.thumb === 'string' ? next.thumb : '';
    if (mode === 'soft') {
      if (url.startsWith('data:') && url.length > 80000 && thumb) next.url = thumb;
      return omitInlineAsset(next);
    }
    if (thumb && (!thumb.startsWith('data:') || thumb.length <= 60000)) {
      next.thumb = thumb;
      next.url = (!url || url.startsWith('data:')) ? thumb : url;
    } else if (url && !url.startsWith('data:')) {
      next.url = url;
      next.thumb = thumb || url;
    } else if (url.startsWith('data:') && url.length <= 40000) {
      next.url = url;
      next.thumb = thumb || url;
    } else {
      next.url = '';
      next.thumb = '';
    }
    delete next.previewRepaired;
    return next;
  });
}

function readImageDimensions(dataUrl) {
  return new Promise(resolve => {
    if (!dataUrl) {
      resolve({ width: 0, height: 0 });
      return;
    }
    const img = new Image();
    img.onload = () => resolve({
      width: img.naturalWidth || 0,
      height: img.naturalHeight || 0,
    });
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = dataUrl;
  });
}

async function readImageFileMeta(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('读取文件失败'));
    reader.readAsDataURL(file);
  });
  const thumb = await createImageDataUrlThumb(dataUrl, 160, 0.72);
  const originUrl = dataUrl.length > 100000
    ? (await createImageDataUrlThumb(dataUrl, 512, 0.78))
    : dataUrl;
  const dimensions = await readImageDimensions(dataUrl);
  const fileExt = file.name.match(/\.(\w+)$/)?.[1]?.toLowerCase() || '';
  return {
    originUrl: originUrl || dataUrl,
    thumbUrl: thumb || originUrl || dataUrl,
    fileExt,
    fileSize: file.size,
    width: dimensions.width,
    height: dimensions.height,
  };
}

async function refreshMaterialLibrary() {
  const context = getMaterialApiContext();
  const { storeId, user } = context;
  await syncQueryMaterialLibrary(platform, storeId, user, context);
  pushEditorSync();
}

function mergePendingTemplatesIntoList(list, pending) {
  return mergeRemoteTemplatesWithPending(list, pending);
}

async function refreshTemplateLibrary() {
  const context = getMaterialApiContext();
  const { storeId, user } = context;
  const writeStoreId = resolveWriteStoreId(storeId);
  const pending = loadPendingTemplates(writeStoreId);
  if (isQueryTemplateView(currentView.value)) {
    await syncQueryTemplateLibrary(platform, storeId, user, context);
    platform.queryTemplates = mergePendingTemplatesIntoList(
      platform.queryTemplates,
      pending,
    );
    pushEditorSync();
    return;
  }
  platform.templates = mergePendingTemplatesIntoList(platform.templates, pending);
  await syncTemplateLibrary(platform, storeId, user, context);
  platform.templates = mergePendingTemplatesIntoList(platform.templates, pending);
  pushEditorSync();
}

function createImageDataUrlThumb(dataUrl, maxSize = 192, quality = 0.72) {
  return new Promise(resolve => {
    if (!dataUrl || typeof dataUrl !== 'string') {
      resolve('');
      return;
    }
    if (dataUrl.startsWith('data:image/svg') || dataUrl.length < 20000) {
      resolve(dataUrl);
      return;
    }
    const img = new Image();
    if (!dataUrl.startsWith('data:') && !dataUrl.startsWith('blob:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => {
      try {
        const scale = Math.min(1, maxSize / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));
        const w = Math.max(1, Math.round((img.naturalWidth || maxSize) * scale));
        const h = Math.max(1, Math.round((img.naturalHeight || maxSize) * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

function compactStateForStorage(state) {
  if (!state) return state;
  try {
    const compact = JSON.parse(JSON.stringify(state));
    const shrinkObjects = objs => {
      (objs || []).forEach(o => {
        if (typeof o.src !== 'string' || !o.src.startsWith('data:')) return;
        if (o.userReplaced) {
          if (o.src.length > 120000) {
            o.src = '';
            o._omitSrc = true;
            o._omitUserSrc = true;
          }
          return;
        }
        if (o.src.length > 2048) {
          o.src = '';
          o._omitSrc = true;
        }
      });
    };
    (compact.pages || []).forEach(p => {
      shrinkObjects(p.objects);
      if (typeof p.bgSrc === 'string' && p.bgSrc.startsWith('data:') && p.bgSrc.length > 2048) {
        p.bgSrc = '';
        p._bgOmitSrc = true;
      }
    });
    shrinkObjects(compact.objects);
    if (compact.meta && typeof compact.meta === 'object') {
      const meta = { ...compact.meta };
      if (typeof meta.layerJson === 'string' && meta.layerJson.length > 4096) {
        meta.layerJson = null;
        if (meta.templateId || meta.fromTemplate) meta._layerJsonOmitted = true;
      }
      delete meta.mockupLayersJson;
      delete meta.extraDesignPages;
      compact.meta = meta;
    }
    return compact;
  } catch {
    return state;
  }
}

export function parseEditorLayerState(source) {
  if (!source) return null;
  if (typeof source === 'object') return source;
  try {
    const parsed = JSON.parse(source);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function hasUsableEditorLayerState(source) {
  const parsed = parseEditorLayerState(source);
  if (!parsed) return false;
  if (Array.isArray(parsed.pages) && parsed.pages.length) {
    return parsed.pages.every(page => page && Array.isArray(page.objects));
  }
  return Array.isArray(parsed.objects);
}

function compactDesignForStorage(d) {
  if (!d) return d;
  const next = {
    ...d,
    previewDataUrl: undefined,
    thumb: typeof d.thumb === 'string' && d.thumb.startsWith('data:') && d.thumb.length > 4096 ? '' : d.thumb,
    state: compactStateForStorage(d.state),
    branches: (d.branches || []).map(branch => ({
      ...branch,
      state: compactStateForStorage(branch.state),
    })),
  };
  if (typeof next.layerJson === 'string' && next.layerJson.length > 40000) {
    next.layerJson = null;
  }
  if (next.state?.meta && typeof next.state.meta.layerJson === 'string' && next.state.meta.layerJson.length > 4096) {
    next.state = {
      ...next.state,
      meta: { ...next.state.meta, layerJson: null, _layerJsonOmitted: true },
    };
  }
  return next;
}

function buildStoragePayload(options = {}) {
  const {
    draftLimit = Infinity,
    designLimit = Infinity,
    standaloneLimit = Infinity,
    compact = false,
    stripAssets = false,
    materialMode = 'soft',
    dropSavedDraftDupes = false,
  } = options;
  const byUpdated = (a, b) => {
    const ta = new Date(a.updated || a.savedAt || 0).getTime();
    const tb = new Date(b.updated || b.savedAt || 0).getTime();
    return tb - ta;
  };
  // 远程设计中心列表只保留在内存中；本地仅缓存编辑器尚待同步的草稿，
  // 避免把服务端 layerJson 列表重复写入 localStorage 触发配额错误。
  let nextDrafts = [...(
    USE_REMOTE_DESIGN_CENTER ? editorDraftCache.value : drafts.value
  )].sort(byUpdated);
  let nextDesigns = platform.designs.filter(d =>
    d.state || d.status === 'done' || d.remoteSaved,
  ).sort(byUpdated);
  let nextStandalone = [...(platform.standaloneDesigns || [])];
  if (dropSavedDraftDupes) {
    const savedIds = new Set([
      ...nextDesigns.map(d => d.id),
      ...nextStandalone.filter(d => d.state).map(d => d.id),
    ]);
    nextDrafts = nextDrafts.filter(d => d?.id && !savedIds.has(d.id));
  }
  if (Number.isFinite(draftLimit)) nextDrafts = nextDrafts.slice(0, draftLimit);
  if (Number.isFinite(designLimit)) nextDesigns = nextDesigns.slice(0, designLimit);
  if (Number.isFinite(standaloneLimit)) {
    const keptIds = new Set(
      nextStandalone.filter(d => d.state).sort(byUpdated).slice(0, standaloneLimit).map(d => d.id)
    );
    nextStandalone = nextStandalone.filter(d => !d.state || keptIds.has(d.id));
  }
  if (compact) {
    nextDrafts = nextDrafts.map(compactDesignForStorage);
    nextDesigns = nextDesigns.map(d => (d.state ? compactDesignForStorage(d) : {
      ...d,
      previewDataUrl: undefined,
      layerJson: typeof d.layerJson === 'string' && d.layerJson.length > 40000 ? null : d.layerJson,
    }));
    nextStandalone = nextStandalone.map(d => (d.state ? compactDesignForStorage(d) : d));
  }
  return {
    drafts: nextDrafts,
    designs: nextDesigns,
    templates: USE_ART_API
      ? undefined
      : (stripAssets ? (platform.templates || []).map(omitInlineAsset) : platform.templates),
    templateFolders: USE_ART_API ? undefined : platform.templateFolders,
    materials: USE_ART_API
      ? undefined
      : (stripAssets ? slimMaterialsForQuota(platform.materials, materialMode) : platform.materials),
    materialFolders: USE_ART_API ? undefined : platform.materialFolders,
    deletedDefaultMaterialIds: USE_ART_API ? undefined : (platform.deletedDefaultMaterialIds || []),
    standaloneDesigns: nextStandalone,
    printConfig: platform.printConfig,
    commonColors: USE_ART_API ? undefined : platform.commonColors,
  };
}

function freeAuxiliaryLocalStorage() {
  const keep = new Set([STORAGE_KEY, 'cdp_role', 'cdp_store']);
  const keys = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key) keys.push(key);
  }
  keys.forEach(key => {
    if (keep.has(key)) return;
    if (!/^(cloud_design|cdp_|__cdp)/i.test(key)) return;
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  });
}

function saveStorage(options = {}) {
  const reportError = options.reportError !== false;
  normalizeTemplateAssetPaths();
  const attempts = [
    { options: { compact: true }, okMsg: null, syncTrim: false },
    {
      options: {
        compact: true,
        stripAssets: true,
        dropSavedDraftDupes: true,
        draftLimit: 1,
        materialMode: 'soft',
      },
      okMsg: '已释放本地缓存空间，保存完成',
      syncTrim: true,
    },
    {
      options: {
        compact: true,
        stripAssets: true,
        dropSavedDraftDupes: true,
        draftLimit: 0,
        designLimit: 12,
        standaloneLimit: 12,
        materialMode: 'hard',
      },
      okMsg: '已释放本地缓存空间，保存完成',
      syncTrim: true,
    },
    {
      options: {
        compact: true,
        stripAssets: true,
        dropSavedDraftDupes: true,
        draftLimit: 0,
        designLimit: 5,
        standaloneLimit: 5,
        materialMode: 'hard',
      },
      okMsg: '已释放本地缓存空间，保存完成',
      syncTrim: true,
      freeAux: true,
    },
  ];
  for (const attempt of attempts) {
    try {
      if (attempt.freeAux) freeAuxiliaryLocalStorage();
      const payload = buildStoragePayload(attempt.options);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      window.dispatchEvent(new CustomEvent('cdp-local-storage-updated'));
      if (attempt.syncTrim) {
        if (USE_REMOTE_DESIGN_CENTER) editorDraftCache.value = payload.drafts;
        else drafts.value = payload.drafts;
        if (Number.isFinite(attempt.options.designLimit)) {
          const keptIds = new Set(payload.designs.map(d => d.id));
          platform.designs = platform.designs.filter(d => !d.state || keptIds.has(d.id));
        }
        if (Number.isFinite(attempt.options.standaloneLimit)) {
          const keptIds = new Set(payload.standaloneDesigns.map(d => d.id));
          platform.standaloneDesigns = platform.standaloneDesigns.filter(d => !d.state || keptIds.has(d.id));
        }
        if (attempt.options.stripAssets) {
          platform.templates = (platform.templates || []).map(omitInlineAsset);
          if (!USE_ART_API) {
            platform.materials = slimMaterialsForQuota(
              platform.materials,
              attempt.options.materialMode || 'soft',
            );
            repairMissingMaterialPreviews(platform);
            normalizeMaterialUrls();
          }
        }
        if (attempt.okMsg) {
          showToast(attempt.okMsg, 'success');
        }
      }
      return true;
    } catch (error) {
      if (error?.name !== 'QuotaExceededError' && !/quota/i.test(String(error?.message || ''))) {
        showToast('保存失败，请稍后重试', 'error');
        return false;
      }
    }
  }
  if (reportError) {
    showToast('本地存储空间不足，本次保存未成功；请先导出设计，再清理本站数据后重试', 'error');
  }
  return false;
}

migrateLegacyTemplateCache();
loadStorage();
if (USE_REMOTE_TEMPLATES) {
  platform.templates = loadPendingTemplates();
  platform.templateFolders = {};
}
if (!USE_ART_API) {
  repairMissingMaterialPreviews(platform);
}
normalizeTemplateAssetPaths();
normalizeMaterialUrls();
bindStorageBridge();
bindFontLibraryBridge();

function getMaterialApiContext() {
  const roleUser = platform.users[session.role] || platform.users.designer;
  return {
    storeId: session.storeId,
    user: roleUser,
    stores: platform.stores || [],
    departmentId: session.departmentId,
    departmentName: session.departmentName,
  };
}

/** 全平台视图下写入模版/素材时，使用当前 session 部门上下文 */
function resolveWriteStoreId(storeId = session.storeId) {
  if (storeId && storeId !== 'all') return storeId;
  if (session.departmentId != null && Number(session.departmentId) > 0) {
    const match = platform.stores?.find((item) => Number(item.departmentId) === Number(session.departmentId));
    if (match?.id) return match.id;
    return toPlatformStoreId(session.departmentId);
  }
  const roleUser = platform.users[session.role] || platform.users.designer;
  return roleUser?.stores?.[0] || platform.stores?.[0]?.id || 's1';
}

function getMaterialApiContextForWrite(storeIdOverride) {
  const roleUser = platform.users[session.role] || platform.users.designer;
  const rawStoreId = storeIdOverride || session.storeId;
  // 全平台视图下保持 storeId='all'，让下游 departmentParams 能正确识别「全平台」
  // 并不传 departmentId / departmentName，由服务端使用第一个门店作为默认值
  const storeId = isAllPlatformStore(rawStoreId)
    ? 'all'
    : resolveWriteStoreId(rawStoreId);
  return {
    storeId,
    user: roleUser,
    stores: platform.stores || [],
    ...(storeId !== 'all' ? {
      departmentId: session.departmentId,
      departmentName: session.departmentName,
    } : {}),
  };
}

function storesApiContext(extra = {}) {
  return { ...getMaterialApiContext(), stores: platform.stores, ...extra };
}

function writeStoresApiContext(storeIdOverride, extra = {}) {
  return { ...getMaterialApiContextForWrite(storeIdOverride), stores: platform.stores, ...extra };
}

const artApiSync = createArtApiSync(platform, drafts, getMaterialApiContext, editorDraftCache);

function isRemoteArtModuleConnected(moduleKey) {
  return USE_ART_API
    && !isUnitTestRuntime()
    && artApiSync.apiStatus[moduleKey] === true;
}

function mustUseRemoteTemplateFolderApi() {
  if (!USE_ART_API || isUnitTestRuntime()) return false;
  const moduleKey = isQueryTemplateView(currentView.value) ? 'queryTemplates' : 'templates';
  return USE_REMOTE_TEMPLATES || isRemoteArtModuleConnected(moduleKey);
}

function mustUseRemoteMaterialFolderApi() {
  if (!USE_ART_API || isUnitTestRuntime()) return false;
  return USE_REMOTE_MATERIALS || isRemoteArtModuleConnected('queryMaterials');
}

function isLocalOnlyFolderId(folderId) {
  const id = String(folderId || '');
  return id.startsWith('f_') || id.startsWith('mf_');
}

function folderIdsEqual(a, b) {
  return String(a ?? '') === String(b ?? '');
}

function showApiErrorToast(error, fallbackMessage) {
  if (error?.toastShown) return;
  showToast(error?.message || fallbackMessage, 'warn');
}

export function usePlatformStore() {
  const orgStoreError = ref('');
  const selectedTemplateFolderId = ref(null);
  const roleKey = computed(() => getRoleKey(session.role));
  const user = computed(() => getOrCreateUserProfile(session.role));
  const activeRoles = computed(() => {
    const list = [];
    if (Array.isArray(session.backendRoles) && session.backendRoles.length) {
      list.push(...session.backendRoles);
    }
    if (Array.isArray(user.value.backendRoles) && user.value.backendRoles.length) {
      list.push(...user.value.backendRoles);
    }
    if (!list.length && session.role) {
      list.push(session.role);
    }
    return list;
  });
  const isPlatformAdmin = computed(() => {
    return roleKey.value === 'platform_admin' || 
      roleKey.value === 'admin' ||
      activeRoles.value.some(r => String(r).toUpperCase() === 'SUPERADMIN' || String(r).toUpperCase() === 'ADMIN');
  });
  const isBoss = computed(() => {
    return roleKey.value === 'boss' || 
      activeRoles.value.some(r => String(r).toUpperCase() === 'BOSS');
  });
  const isStoreManager = computed(() => {
    return roleKey.value === 'store_manager' || 
      roleKey.value === 'manager' ||
      roleKey.value === 'shop_keeper' ||
      activeRoles.value.some(r => String(r).toUpperCase() === 'SHOP_KEEPER' || String(r).toUpperCase() === 'STORE_MANAGER');
  });
  const isDesignerLeader = computed(() => {
    return roleKey.value === 'designer_leader' || 
      activeRoles.value.some(r => String(r).toUpperCase() === 'DESIGNER_LEADER');
  });

  const hasAllStoresAccess = computed(() => {
    return isPlatformAdmin.value || isBoss.value;
  });

  // 1. 导入 PDF 模板权限：仅管理员、老板、店长有权限
  const canImportPdfTemplate = computed(() => {
    return isPlatformAdmin.value || isBoss.value || isStoreManager.value || !!perms.value.importPdfTemplate;
  });

  const perms = computed(() => resolveMergedRolePerms(activeRoles.value));
  const navItems = computed(() => resolveMergedRoleNav(activeRoles.value));
  // 仅 BOSS / SUPERADMIN 可查看「全平台」店铺视图
  const canViewAllStores = computed(() => hasFullStoreAccessRole(activeRoles.value));
  const currentStore = computed(() => {
    if (session.storeId === 'all' && hasAllStoresAccess.value) return { id: 'all', name: '全平台' };
    return platform.stores.find(s => s.id === session.storeId)
      || platform.stores[0]
      || { id: session.storeId || 's1', name: session.departmentName || `店铺${String(session.storeId || '1').replace(/^s/i, '')}` };
  });
  const accessibleStores = computed(() => {
    if (hasAllStoresAccess.value) {
      return platform.stores.length ? platform.stores : [{ id: 's1', name: '鲸夕总店' }];
    }
    const userStores = user.value.stores || [];
    const list = platform.stores.filter(s => userStores.includes(s.id));
    if (list.length) return list;
    if (userStores.length && platform.stores.length) {
      const match = platform.stores.find(s => s.id === userStores[0]);
      if (match) return [match];
    }
    const fallbackId = userStores[0] || 's1';
    const fallbackName = session.departmentName || platform.stores.find(s => s.id === fallbackId)?.name || '默认店铺';
    return [{ id: fallbackId, name: fallbackName }];
  });
  const organizationTree = computed(() => {
    const rawTree = platform.organizationTree || { id: '74', name: '店铺管理', groups: [], totalStores: 0 };
    if (hasAllStoresAccess.value) {
      return rawTree;
    }
    const allowedStoreIds = new Set(accessibleStores.value.map(s => s.id));
    const filteredGroups = (rawTree.groups || [])
      .map(g => ({
        ...g,
        stores: (g.stores || []).filter(s => allowedStoreIds.has(s.id)),
      }))
      .filter(g => g.stores.length > 0);
    return {
      ...rawTree,
      groups: filteredGroups,
      totalStores: accessibleStores.value.length,
    };
  });
  const printConfig = computed(() => getPrintConfig(platform, session.storeId));
  const isCurrentViewLoading = computed(() => {
    const moduleKey = getViewModuleKey(currentView.value);
    return moduleKey ? artApiSync.moduleLoading[moduleKey] : false;
  });

  function isKnownStoreId(storeId) {
    if (!storeId) return false;
    if (storeId === 'all') return hasAllStoresAccess.value;
    const normalized = toPlatformStoreId(storeId);
    const allowedIds = new Set([
      ...(user.value.stores || []),
      ...(accessibleStores.value || []).map((item) => item.id),
    ]);
    return allowedIds.has(normalized) || allowedIds.has(storeId);
  }

  function resolveAllowedStoreId(storeId) {
    const raw = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : null;
    if (raw === 'all') {
      // 仅 BOSS / SUPERADMIN 可切换到全平台视图
      if (canViewAllStores.value) return 'all';
      // 非授权角色回退到第一个可访问店铺
      return toPlatformStoreId(user.value.stores?.[0] || platform.stores?.[0]?.id || 's1');
    }
    if (raw && isKnownStoreId(raw)) {
      return toPlatformStoreId(raw);
    }
    if (canViewAllStores.value) {
      return 'all';
    }
    return toPlatformStoreId(user.value.stores?.[0] || accessibleStores.value?.[0]?.id || 's1');
  }

  function ensureFontsLoaded() {
    return loadFonts();
  }

  async function addPlatformFontFiles(files) {
    const list = [...(files || [])];
    if (!list.length) {
      return { added: [], duplicates: [], errors: [] };
    }

    if (USE_FONT_API && fontApiAvailable.value && !isUnitTestRuntime()) {
      const added = [];
      const duplicates = [];
      const errors = [];
      for (const file of list) {
        try {
          const oss = await uploadFontFileToOss(file, 0);
          const fontName = String(file.name || 'font').replace(/\.[^.]+$/, '') || 'font';
          const fileType = String(file.name || '').split('.').pop()?.toLowerCase() || '';
          const body = mapPlatformFontToUploadPayload({
            name: fontName,
            family: fontName,
            fileType,
            size: file.size,
            previewText: fontName,
          }, oss.url);
          const payload = await fontService.upload(body);
          const result = unwrapApiResult(payload);
          added.push(
            typeof result === 'object' && result && (result.fontFamily || result.fileUrl || result.id)
              ? mapFontDtoToPlatform(result)
              : mapFontDtoToPlatform({
                id: result,
                fontName,
                fontFamily: fontName,
                fileUrl: oss.url,
                fileType,
                fileSize: file.size,
                previewText: body.previewText,
              }),
          );
        } catch (error) {
          errors.push({ fileName: file.name, reason: error.message || '上传失败' });
        }
      }
      await loadFonts({ force: true });
      return { added, duplicates, errors };
    }

    if (!window.CdpFontLibrary?.addFiles) {
      return {
        added: [],
        duplicates: [],
        errors: [{ fileName: '', reason: '字体本地存储服务未加载' }],
      };
    }
    const result = await window.CdpFontLibrary.addFiles(list);
    await loadFonts({ force: true });
    return result;
  }

  async function addPlatformFonts(list) {
    return addPlatformFontFiles(list);
  }

  function fontMatchesObject(font, object) {
    if (!font || object?.type !== 'text') return false;
    if (object.fontAssetId && object.fontAssetId === font.id) return true;
    const family = String(font.family || '').replace(/["'\s]/g, '').toLowerCase();
    const postScriptName = String(font.postScriptName || '').replace(/["'\s]/g, '').toLowerCase();
    const objectFamily = String(object.fontFamily || '').replace(/["'\s]/g, '').toLowerCase();
    const sourceFont = String(object.sourceFont || '').replace(/["'\s]/g, '').toLowerCase();
    if (postScriptName && sourceFont === postScriptName) return true;
    const familyMatches = family && (objectFamily.includes(family) || sourceFont === family);
    if (!familyMatches) return false;
    const weightMatches = Number(object.fontWeight || 400) === Number(font.weight || 400);
    const styleMatches = String(object.fontStyle || 'normal') === String(font.style || 'normal');
    return weightMatches && styleMatches;
  }

  function walkFontObjects(value, callback, seen = new WeakSet()) {
    if (!value || typeof value !== 'object' || seen.has(value)) return;
    seen.add(value);
    if (value.type === 'text') callback(value);
    if (Array.isArray(value)) {
      value.forEach(item => walkFontObjects(item, callback, seen));
      return;
    }
    Object.values(value).forEach(item => walkFontObjects(item, callback, seen));
  }

  function getPlatformFontUsage(id) {
    const font = platformFonts.value.find(item => item.id === id);
    if (!font) return [];
    const sources = [
      ...drafts.value.map(item => ({ item, label: item.songName || item.name || item.orderId || '草稿稿件' })),
      ...platform.designs.map(item => ({ item, label: item.songName || item.name || item.orderId || '已保存设计' })),
      ...(platform.standaloneDesigns || []).map(item => ({ item, label: item.designName || item.name || '自由设计' })),
      ...platform.templates.map(item => ({ item, label: `模版：${item.name || item.id}` })),
    ];
    return sources.map(({ item, label }) => {
      let count = 0;
      walkFontObjects(item, object => {
        if (fontMatchesObject(font, object)) count += 1;
      });
      return { id: item.id, label, count };
    }).filter(usage => usage.count > 0);
  }

  function replacementFontFamily(font) {
    return `"${font.family}", "Microsoft YaHei", "Noto Sans SC", sans-serif`;
  }

  function replaceFontUsage(font, replacement) {
    let replaced = 0;
    [drafts.value, platform.designs, platform.standaloneDesigns || [], platform.templates].forEach(root => {
      walkFontObjects(root, object => {
        if (!fontMatchesObject(font, object)) return;
        object.fontFamily = replacementFontFamily(replacement);
        object.sourceFont = replacement.postScriptName || replacement.family;
        object.fontWeight = replacement.weight || object.fontWeight || 400;
        object.fontStyle = replacement.style || 'normal';
        if (replacement.source === 'system') delete object.fontAssetId;
        else object.fontAssetId = replacement.id;
        replaced += 1;
      });
    });
    if (replaced) saveStorage();
    return replaced;
  }

  async function checkPlatformFontMissing(fontFamilies = []) {
    const families = [...new Set((fontFamilies || []).map(item => String(item || '').trim()).filter(Boolean))];
    if (!families.length) return [];

    if (USE_FONT_API && fontApiAvailable.value) {
      try {
        const payload = await fontService.checkMissing(families);
        const result = unwrapApiResult(payload);
        if (Array.isArray(result)) return result;
        if (Array.isArray(result?.missing)) return result.missing;
        return [];
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[fonts] 缺失字体检测失败，改用本地比对', error);
        }
      }
    }

    const installed = new Set(
      platformFonts.value.flatMap(font => [font.family, font.name, font.postScriptName]
        .map(item => normalizeFontKey(item))
        .filter(Boolean)),
    );
    const systemFontKeys = new Set([
      'arial', 'helvetica', 'simsun', 'simhei', 'microsoftyahei', 'msyh', 'sans-serif', 'notosanssc',
    ]);
    return families.filter(name => {
      const key = normalizeFontKey(name);
      if (!key || systemFontKeys.has(key)) return false;
      if (installed.has(key)) return false;
      return ![...installed].some(item => item.includes(key) || key.includes(item));
    });
  }

  async function removePlatformFont(id, replacementId = '') {
    if (!perms.value.deleteFonts) {
      showToast('仅平台管理员可删除字体', 'warn');
      return { ok: false, reason: 'permission' };
    }
    const font = platformFonts.value.find(item => item.id === id && item.source !== 'system');
    if (!font) return { ok: false, reason: 'not-found' };
    const usage = getPlatformFontUsage(id);
    let replaced = 0;
    if (usage.length) {
      const replacement = platformFonts.value.find(item => item.id === replacementId && item.id !== id);
      if (!replacement) return { ok: false, reason: 'in-use', usage };
      replaced = replaceFontUsage(font, replacement);
    }
    if (USE_FONT_API && fontApiAvailable.value && font.source === 'upload') {
      try {
        await fontService.remove(id);
      } catch (error) {
        showToast(error.message || '远程字体删除失败', 'warn');
        return { ok: false, reason: 'remote-delete-failed' };
      }
    } else if (window.CdpFontLibrary?.remove) {
      await window.CdpFontLibrary.remove(id);
    }
    await loadFonts({ force: true });
    return { ok: true, usage, replaced };
  }

  function applyAuthProfile(role, authProfile) {
    if (!authProfile || typeof authProfile !== 'object') return;
    if (authProfile.token) setAuthToken(authProfile.token);
    const profile = platform.users[role] || getOrCreateUserProfile(role);
    if (authProfile.userName) {
      profile.name = authProfile.userName;
      profile.avatar = String(authProfile.userName).slice(0, 1) || profile.avatar;
      session.userName = authProfile.userName;
    }
    if (authProfile.userId != null) {
      profile.id = String(authProfile.userId);
      session.userId = authProfile.userId;
    }
    if (authProfile.weChatWorkAccount) {
      profile.weChatWorkAccount = authProfile.weChatWorkAccount;
      session.weChatWorkAccount = authProfile.weChatWorkAccount;
    }
    if (Array.isArray(authProfile.roles)) {
      profile.backendRoles = [...authProfile.roles];
      session.backendRoles = [...authProfile.roles];
    }
    if (authProfile.roleTitle) {
      profile.roleLabel = authProfile.roleTitle;
    }
  }

  // 路由守卫与登录流程都会拉组织树，这里合并同时进行的请求，避免重复覆写 session.storeId
  let orgStoreLoadPromise = null;

  async function loadOrganizationStores(options = {}) {
    if (!USE_ART_API || isUnitTestRuntime()) return platform.stores;
    if (orgStoreLoadPromise) return orgStoreLoadPromise;
    orgStoreLoadPromise = doLoadOrganizationStores(options)
      .finally(() => { orgStoreLoadPromise = null; });
    return orgStoreLoadPromise;
  }

  async function doLoadOrganizationStores(options = {}) {
    const profile = platform.users[session.role] || getOrCreateUserProfile(session.role);
    try {
      const result = await syncOrganizationStores(platform, profile, getRoleKey(session.role), options);
      if (!result.stores || result.stores.length === 0) {
        orgStoreError.value = '没有绑定门店信息，请绑定所属门店信息再操作';
      } else {
        orgStoreError.value = '';
      }
      const persistedStoreId = typeof localStorage !== 'undefined'
        ? localStorage.getItem('cdp_store')
        : null;
      session.storeId = resolveAllowedStoreId(persistedStoreId || session.storeId);
      applySessionDepartment(session, session.storeId, platform.stores, localStorage);
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('cdp_store', session.storeId);
      }
      pushEditorSync();
      return platform.stores;
    } catch (error) {
      orgStoreError.value = '没有绑定门店信息，请绑定所属门店信息再操作';
      if (import.meta.env.DEV) console.warn('[org-api] 组织树门店加载失败，保留本地店铺列表', error);
      return platform.stores;
    }
  }

  async function login(role, authProfile = null) {
    clearLogoutFlag();
    const targetRole = getRoleKey(role || authProfile?.loginRole) || 'designer';
    session.role = targetRole;
    applyAuthProfile(session.role, authProfile);
    if (authProfile?.token) {
      setAuthToken(authProfile.token);
    }
    await loadOrganizationStores();
    session.storeId = resolveAllowedStoreId(localStorage.getItem('cdp_store'));
    applySessionDepartment(session, session.storeId, platform.stores, localStorage);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('cdp_role', session.role);
      localStorage.setItem('cdp_store', session.storeId);
      if (authProfile) {
        localStorage.setItem('cdp_raw_roles', JSON.stringify(authProfile.roles || []));
        localStorage.setItem('cdp_user', JSON.stringify({
          userId: authProfile.userId,
          userName: authProfile.userName,
          weChatWorkAccount: authProfile.weChatWorkAccount,
          roles: authProfile.roles,
          primaryRole: authProfile.primaryRole,
          roleTitle: authProfile.roleTitle,
          loginRole: session.role,
        }));
      }
    }
    currentView.value = navItems.value[0] || 'templates';
    showToast(`欢迎，${user.value.name}`, 'success');
    pushRoute(`/platform/${currentView.value}`);
  }

  /** URL / 缓存 token 免扫码登录：从 JWT 解析用户与角色并进入平台 */
  async function tryAutoLoginFromToken(token = getAuthToken()) {
    if (!token || isUnitTestRuntime()) return false;
    const authProfile = mapJwtPayloadToAuthSession(token);
    if (!authProfile?.loginRole) return false;
    await login(authProfile.loginRole, authProfile);
    return true;
  }

  function logout() {
    clearAuthToken();
    session.role = 'designer';
    session.userName = '';
    session.userId = null;
    session.weChatWorkAccount = '';
    session.backendRoles = [];
    session.departmentId = null;
    session.departmentName = '';
    session.storeId = 's1';
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('cdp_role');
      localStorage.removeItem('cdp_raw_roles');
      localStorage.removeItem('cdp_user');
      localStorage.removeItem('wecom_token');
      localStorage.removeItem('cdp_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('cdp_store');
      localStorage.removeItem('cdp_organization_tree');
      localStorage.removeItem('cdp_department_id');
      localStorage.removeItem('cdp_department_name');
      localStorage.removeItem('active_editor_meta');
      localStorage.removeItem('editor_return_route');
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('jx_editor_layer_json_') || key.startsWith('cdp_') || key.startsWith('wecom_')) {
          localStorage.removeItem(key);
        }
      });
      localStorage.setItem('user_logged_out', '1');
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.clear();
      sessionStorage.setItem('user_logged_out', '1');
    }
    platform.queryTemplates = [];
    platform.queryMaterials = [];
    platform.templateFolders = {};
    platform.materialFolders = {};
    editorMeta.value = null;
    currentView.value = 'templates';
    pushRoute('/login');
  }

  function switchStore(storeId) {
    session.storeId = resolveAllowedStoreId(storeId);
    applySessionDepartment(session, session.storeId, platform.stores, localStorage);
    localStorage.setItem('cdp_store', session.storeId);
    showToast(`已切换至 ${currentStore.value.name}`, 'success');
    if (USE_REMOTE_TEMPLATES && !isUnitTestRuntime()) {
      const pending = loadPendingTemplates(session.storeId);
      const pendingIds = new Set(pending.map(item => String(item.id)));
      platform.templates = [
        ...platform.templates.filter(item => !item.pendingPersistence || !pendingIds.has(String(item.id))),
        ...pending,
      ];
    }
    if (USE_ART_API && !isUnitTestRuntime()) {
      artApiSync.loadForView(currentView.value, { force: true });
    }
  }

  function navigate(view) {
    if (!navItems.value.includes(view)) view = navItems.value[0];
    currentView.value = view;
    if (!isUnitTestRuntime()) {
      artApiSync.loadForView(view);
      if (view === 'fonts') {
        loadFonts();
      }
    }
  }

  function activeTemplateItems() {
    return isQueryTemplateView(currentView.value) ? platform.queryTemplates : platform.templates;
  }

  function replaceActiveTemplateItems(items) {
    if (isQueryTemplateView(currentView.value)) platform.queryTemplates = items;
    else platform.templates = items;
  }

  function activeMaterialItems() {
    return (currentView.value === 'materials' || currentView.value === 'materials1') ? platform.queryMaterials : platform.materials;
  }

  function replaceActiveMaterialItems(items) {
    if (currentView.value === 'materials' || currentView.value === 'materials1') platform.queryMaterials = items;
    else platform.materials = items;
  }

  function storeTemplates() {
    const storageKey = libraryStorageKey();
    const items = activeTemplateItems();
    let visible;
    if (isQueryTemplateView(currentView.value)) {
      visible = session.storeId === 'all'
        ? [...items]
        : items.filter(item =>
          item.storeId === storageKey
          || item.storeId === 'all'
          || item.storeId === 's0'
          || item.type === 'master'
          || item.scope === 'master'
        );
    } else if (session.storeId === 'all') {
      visible = [...items];
    } else {
      visible = items.filter(item =>
        item.storeId === storageKey
        || item.storeId === session.storeId
        || item.type === 'master'
        || item.scope === 'master'
      );
    }
    const seen = new Set();
    return visible.filter((item) => {
      const id = String(item?.id || '');
      if (!id || seen.has(id)) return false;
      if (isSpawnedTemplateCopyName(item.name)) return false;
      seen.add(id);
      return true;
    });
  }

  function storeTemplateTotalCount() {
    if (!isQueryTemplateView(currentView.value)) {
      return storeTemplates().length;
    }
    const sid = libraryStorageKey();
    const count = platform.queryTemplateTotalCount?.[sid];
    return count != null ? count : storeTemplates().length;
  }

  function activeTemplateStoreId() {
    if (session.storeId && session.storeId !== 'all') {
      return toPlatformStoreId(resolveShopId(session.storeId, user.value, platform.stores || []));
    }
    return toPlatformStoreId(resolveShopId(user.value.stores?.[0] || 's1', user.value, platform.stores || []));
  }

  function libraryStorageKey() {
    return resolvePlatformStorageKey(session.storeId, user.value, platform.stores || []);
  }

  function storeTemplateFolders() {
    const sid = libraryStorageKey();
    const folderMap = isQueryTemplateView(currentView.value)
      ? platform.queryTemplateFolders
      : platform.templateFolders;
    if (isQueryTemplateView(currentView.value)) {
      if (session.storeId === 'all') {
        if (Array.isArray(folderMap['all']) && folderMap['all'].length > 0) {
          return folderMap['all'];
        }
        const allFolders = Object.values(folderMap).flat().filter(Boolean);
        const seenIds = new Set();
        return allFolders.filter((f) => {
          if (!f?.id || seenIds.has(String(f.id))) return false;
          seenIds.add(String(f.id));
          return true;
        });
      }
      if (!Array.isArray(folderMap[sid])) folderMap[sid] = [];
      return folderMap[sid];
    }
    if (Array.isArray(folderMap[sid]) && folderMap[sid].length > 0) {
      return folderMap[sid];
    }
    if (session.storeId !== 'all' && Array.isArray(folderMap['s1']) && folderMap['s1'].length > 0) {
      return folderMap['s1'];
    }
    if (!Array.isArray(folderMap[sid])) {
      folderMap[sid] = [];
    }
    return folderMap[sid];
  }

  async function createTemplateFolder(rawName, targetStoreId) {
    if (!perms.value.uploadTpl) {
      showToast('当前角色无新建模版文件夹权限', 'warn');
      return null;
    }
    const name = String(rawName || '').trim();
    if (!name) {
      showToast('请输入文件夹名称', 'warn');
      return null;
    }
    const folders = storeTemplateFolders();
    if (folders.some(f => f.name === name)) {
      showToast('已有同名文件夹', 'warn');
      return null;
    }
    if (mustUseRemoteTemplateFolderApi()) {
      try {
        const ctx = writeStoresApiContext(targetStoreId);
        const folderId = await createTemplateFolderRemote(ctx.storeId, ctx.user, name, undefined, platform.stores, ctx);
        if (!folderId) {
          throw new Error('创建文件夹失败：服务端未返回有效 ID');
        }
        await refreshTemplateLibrary();
        const refreshedFolders = storeTemplateFolders();
        let folder = refreshedFolders.find(item => String(item.id) === String(folderId));
        if (!folder) {
          folder = {
            id: String(folderId),
            name,
            ...(isQueryTemplateView(currentView.value) ? { pendingQuerySync: true } : {}),
          };
          refreshedFolders.push(folder);
        }
        showToast(`已创建文件夹「${name}」`, 'success');
        return folder;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 创建模版文件夹失败', error);
        try {
          await refreshTemplateLibrary();
          const synchronized = storeTemplateFolders().find(folder => folder.name === name);
          if (synchronized) {
            showToast(`已同步文件夹「${name}」`, 'success');
            return synchronized;
          }
        } catch { /* 保留原始创建错误 */ }
        showApiErrorToast(error, '创建文件夹失败，请稍后重试');
        return null;
      }
    }
    const folder = { id: 'f_' + Date.now(), name };
    folders.push(folder);
    saveStorage();
    showToast(`已创建文件夹「${name}」`, 'success');
    return folder;
  }

  async function renameTemplateFolder(folderId, rawName) {
    if (!perms.value.uploadTpl) {
      showToast('当前角色无模版库管理权限', 'warn');
      return null;
    }
    const folders = storeTemplateFolders();
    const folder = folders.find(f => folderIdsEqual(f.id, folderId));
    if (!folder) return null;
    const name = String(rawName || '').trim();
    if (!name) return null;
    if (folders.some(f => !folderIdsEqual(f.id, folderId) && f.name === name)) {
      showToast('已有同名文件夹', 'warn');
      return null;
    }
    if (mustUseRemoteTemplateFolderApi() && !isLocalOnlyFolderId(folderId)) {
      try {
        await renameTemplateFolderRemote(folderId, name);
        folder.name = name;
        pushEditorSync();
        showToast(`文件夹已重命名为「${name}」`, 'success');
        return folder;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 重命名模版文件夹失败', error);
        showToast('重命名失败，请稍后重试', 'warn');
        return null;
      }
    }
    folder.name = name;
    saveStorage();
    showToast(`文件夹已重命名为「${name}」`, 'success');
    return folder;
  }

  async function deleteTemplateFolder(folderId) {
    if (!perms.value.uploadTpl) {
      showToast('当前角色无模版库管理权限', 'warn');
      return false;
    }
    const folders = storeTemplateFolders();
    const idx = folders.findIndex(f => folderIdsEqual(f.id, folderId));
    if (idx < 0) {
      showToast('未找到该文件夹，请刷新后重试', 'warn');
      return false;
    }
    const name = folders[idx].name;
    if (mustUseRemoteTemplateFolderApi() && !isLocalOnlyFolderId(folderId)) {
      try {
        await deleteTemplateFolderRemote(folderId);
        await refreshTemplateLibrary();
        showToast(`已删除文件夹「${name}」`, 'success');
        return true;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 删除模版文件夹失败', error);
        showToast('删除文件夹失败，请稍后重试', 'warn');
        return false;
      }
    }
    folders.splice(idx, 1);
    activeTemplateItems().forEach(t => {
      if (materialFolderIdsEqual(t.folderId, folderId)) delete t.folderId;
    });
    saveStorage();
    showToast(`已删除文件夹「${name}」`, 'success');
    return true;
  }

  function folderTemplateCount(folderId) {
    const target = normalizeTemplateFolderId(folderId);
    return storeTemplates().filter(t => normalizeTemplateFolderId(t.folderId) === target).length;
  }

  function canManageTemplates() {
    return !!perms.value.uploadTpl;
  }

  async function moveTemplateToFolder(templateId, folderId) {
    if (!canManageTemplates()) {
      showToast('当前角色无模版库管理权限', 'warn');
      return false;
    }
    const tpl = activeTemplateItems().find(t => String(t.id) === String(templateId));
    if (!tpl) return false;
    if (folderId) {
      const folders = storeTemplateFolders();
      if (!folders.some(f => String(f.id) === String(folderId))) return false;
    }
    if (mustUseRemoteTemplateFolderApi() && !tpl.pendingPersistence) {
      try {
        await moveTemplateToFolderRemote(templateId, folderId);
        if (folderId) tpl.folderId = normalizeTemplateFolderId(folderId);
        else delete tpl.folderId;
        try {
          await refreshTemplateLibrary();
        } catch { /* 忽略刷新异常，本地已更新 */ }
        pushEditorSync();
        return true;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 移动模版失败', error);
        showApiErrorToast(error, '移动模版失败，请稍后重试');
        return false;
      }
    }
    if (folderId) tpl.folderId = normalizeTemplateFolderId(folderId);
    else delete tpl.folderId;
    if (tpl.pendingPersistence) savePendingTemplate(tpl);
    saveStorage();
    pushEditorSync();
    return true;
  }

  async function batchMoveTemplates(templateIds = [], folderId) {
    const ids = [...new Set(templateIds.filter(Boolean))];
    if (!ids.length) return false;
    if (!canManageTemplates()) {
      showToast('当前角色无模版库管理权限', 'warn');
      return false;
    }
    if (folderId) {
      const folders = storeTemplateFolders();
      if (!folders.some(f => String(f.id) === String(folderId))) return false;
    }
    if (mustUseRemoteTemplateFolderApi()) {
      try {
        await batchMoveTemplatesRemote(folderId, ids);
        const templateItems = activeTemplateItems();
        ids.forEach((id) => {
          const tpl = templateItems.find(item => String(item.id) === String(id));
          if (!tpl) return;
          if (folderId) tpl.folderId = normalizeTemplateFolderId(folderId);
          else delete tpl.folderId;
        });
        try {
          await refreshTemplateLibrary();
        } catch { /* 忽略刷新异常，本地已更新 */ }
        pushEditorSync();
        return true;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 批量移动模版失败', error);
        showApiErrorToast(error, '批量移动失败，请稍后重试');
        return false;
      }
    }
    const templateItems = activeTemplateItems();
    ids.forEach((id) => {
      const tpl = templateItems.find(item => String(item.id) === String(id));
      if (!tpl) return;
      if (folderId) tpl.folderId = normalizeTemplateFolderId(folderId);
      else delete tpl.folderId;
      if (tpl.pendingPersistence) savePendingTemplate(tpl);
    });
    saveStorage();
    pushEditorSync();
    return true;
  }

  function canDeleteTemplate(tpl) {
    if (!tpl || !canManageTemplates()) return false;
    if (tpl.type === 'master') return !!perms.value.manageMasterTpl;
    return !!(perms.value.editStoreTpl || perms.value.uploadTpl);
  }

  function canRenameTemplate(tpl) {
    return canDeleteTemplate(tpl);
  }

  async function renameTemplate(templateId, rawName) {
    const templateItems = activeTemplateItems();
    const tpl = templateItems.find(item => item.id === templateId);
    if (!tpl) return false;
    if (!canRenameTemplate(tpl)) {
      showToast('当前角色无重命名该模版权限', 'warn');
      return false;
    }
    const name = String(rawName || '').trim();
    if (!name) {
      showToast('请输入模版名称', 'warn');
      return false;
    }
    const duplicate = templateItems.some(item =>
      item.id !== templateId
      && item.storeId === tpl.storeId
      && (item.folderId || undefined) === (tpl.folderId || undefined)
      && item.name === name
    );
    if (duplicate) {
      showToast('当前位置已有同名模版', 'warn');
      return false;
    }
    if (USE_REMOTE_TEMPLATES && !String(templateId).startsWith('t_')) {
      try {
        await renameTemplateRemote(templateId, name, tpl.thumb);
        tpl.name = name;
        pushEditorSync();
        showToast(`模版已重命名为「${name}」`, 'success');
        return true;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 模版重命名失败', error);
        showToast('重命名失败，请稍后重试', 'warn');
        return false;
      }
    }
    tpl.name = name;
    if (tpl.pendingPersistence) savePendingTemplate(tpl);
    saveStorage();
    showToast(`模版已重命名为「${name}」`, 'success');
    return true;
  }

  async function deleteTemplate(templateId) {
    const templateItems = activeTemplateItems();
    const tpl = templateItems.find(t => t.id === templateId);
    if (!tpl) return false;
    if (!canDeleteTemplate(tpl)) {
      showToast('当前角色无删除该模版权限', 'warn');
      return false;
    }
    if (USE_REMOTE_TEMPLATES && !String(templateId).startsWith('t_')) {
      try {
        await deleteTemplateRemote(templateId);
        removePendingTemplate(templateId);
        replaceActiveTemplateItems(templateItems.filter(t => t.id !== templateId));
        pushEditorSync();
        showToast(`已删除模版「${tpl.name}」`, 'success');
        return true;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 远程删除模版失败', error);
        showToast('删除模版失败，请稍后重试', 'warn');
        return false;
      }
    }
    replaceActiveTemplateItems(templateItems.filter(t => t.id !== templateId));
    removePendingTemplate(templateId);
    if (String(templateId).startsWith('t_pdf_')) {
      fetchCdpEngineJson(`${CDP_ENDPOINTS.templatePdfCache}?templateId=${encodeURIComponent(templateId)}`, {
        method: 'DELETE',
      }, {
        timeoutMs: 5000,
        fallbackMessage: '清理 PDF 模版缓存失败',
      }).catch(() => {});
    }
    saveStorage();
    showToast(`已删除模版「${tpl.name}」`, 'success');
    return true;
  }

  function storeDesigns() {
    const isDesigner = roleKey.value === 'designer';
    const stores = user.value.stores || [];
    if (isDesigner) {
      return platform.designs.filter(d => (stores.length === 0 || stores.includes(d.storeId)) && !d.parentDesignId);
    }
    return platform.designs.filter(d => d.storeId === session.storeId && !d.parentDesignId);
  }

  function allSavedDesigns() {
    const stores = user.value.stores || [];
    const sid = session.storeId === 'all' ? null : session.storeId;
    const isDesigner = roleKey.value === 'designer';
    const account = session.role;
    const accountName = user.value.name;

    const remoteSaved = USE_REMOTE_DESIGN_CENTER
      ? drafts.value.filter(d =>
        d.templateType === DESIGN_CENTER_TEMPLATE_TYPE.SAVED || d.status === 'done')
      : [];

    // 远程模式下只信设计中心列表，禁止再混入 localStorage 的 platform.designs，
    // 否则角标会比接口 total 多出一截历史脏数据。
    const allDesigns = USE_REMOTE_DESIGN_CENTER
      ? remoteSaved
      : [
        ...platform.designs.filter(d => d.state && !d.parentDesignId),
        ...(platform.standaloneDesigns || []).filter(d => d.state),
        ...remoteSaved,
      ];

    const seen = new Set();
    return allDesigns.filter(d => {
      if (!d?.id || seen.has(d.id)) return false;
      seen.add(d.id);
      // 远程设计中心列表已由后端按登录态与部门做数据权限隔离，
      // 不再用前端组织树店铺白名单二次过滤，否则会隐藏跨店铺的历史合法稿件。
      if (!d.remoteDesignCenter) {
        if (stores.length > 0 && !stores.includes(d.storeId)) return false;
        if (sid && d.storeId !== sid) return false;
      }
      if (!isDesigner && d.orderId) return false;
      if (!belongsToCurrentAccount(d, account, roleKey.value, accountName)) return false;
      return true;
    }).sort((a, b) => {
      const timeA = new Date(a.updated || 0).getTime();
      const timeB = new Date(b.updated || 0).getTime();
      return timeB - timeA;
    });
  }

  function allDrafts() {
    const stores = user.value.stores || [];
    const sid = session.storeId === 'all' ? null : session.storeId;
    const isDesigner = roleKey.value === 'designer';
    const account = session.role;
    const accountName = user.value.name;
    const savedIds = USE_REMOTE_DESIGN_CENTER
      ? new Set(
        drafts.value
          .filter(d => d.templateType === DESIGN_CENTER_TEMPLATE_TYPE.SAVED || d.status === 'done')
          .map(d => d.id),
      )
      : new Set([
        ...platform.designs.filter(d => d.state).map(d => d.id),
        ...(platform.standaloneDesigns || []).filter(d => d.state).map(d => d.id),
      ]);

    return drafts.value.filter(d => {
      if (!d?.id || savedIds.has(d.id)) return false;
      if (USE_REMOTE_DESIGN_CENTER) {
        if (d.templateType === DESIGN_CENTER_TEMPLATE_TYPE.SAVED || d.status === 'done') return false;
      } else if (d.draftStatus === 1 || d.status === 'done') {
        return false;
      }
      // 远程设计中心列表已由后端做数据权限隔离，跳过前端店铺白名单二次过滤。
      if (!d.remoteDesignCenter) {
        if (stores.length > 0 && !stores.includes(d.storeId)) return false;
        if (sid && d.storeId !== sid) return false;
      }
      if (!isDesigner && d.orderId) return false;
      if (!belongsToCurrentAccount(d, account, roleKey.value, accountName)) return false;
      return true;
    }).sort((a, b) => {
      const timeA = new Date(a.updated || a.savedAt || 0).getTime();
      const timeB = new Date(b.updated || b.savedAt || 0).getTime();
      return timeB - timeA;
    });
  }

  function activeMaterialStoreId() {
    if (session.storeId === 'all') return 'all';
    return toPlatformStoreId(resolveShopId(session.storeId, user.value, platform.stores || []));
  }

  function isPlatformScopeMaterial(material) {
    if (!material) return false;
    return material.storeId === 'all'
      || material.storeId === 's0'
      || material.scope === 'platform'
      || Number(material.departmentId) === 0;
  }

  function materialsAtUploadLocation(folderId) {
    const targetFolderId = normalizeMaterialFolderId(folderId);
    const pool = session.storeId === 'all' ? storeMaterials() : activeMaterialItems();
    return pool.filter((item) => normalizeMaterialFolderId(item.folderId) === targetFolderId);
  }

  function isMaterialVisibleForStore(material, storeId) {
    if (!material) return false;
    if (material.storeId === storeId) return true;
    if (material.storeId === 's0' || material.scope === 'platform') return true;
    return Number(material.departmentId) === 0;
  }

  function canManageMaterials() {
    return !!perms.value.manageMaterials;
  }

  function materialPermissionContext() {
    return {
      backendRoles: activeRoles.value,
      loginRoleKey: session.role,
      userId: user.value?.id,
      sessionUserId: session.userId,
    };
  }

  function canDeleteAnyMaterial() {
    return hasDeleteAnyMaterialAccess(materialPermissionContext());
  }

  function canDeleteMaterial(material) {
    return canDeleteMaterialByPolicy(material, materialPermissionContext());
  }

  function storeMaterials() {
    const items = activeMaterialItems();
    const storageKey = libraryStorageKey();
    const useQueryMaterials = currentView.value === 'materials' || currentView.value === 'materials1';

    if (useQueryMaterials) {
      if (session.storeId === 'all') return [...items];
      return items.filter(material =>
        material.storeId === storageKey
        || material.storeId === 'all'
        || material.storeId === 's0'
        || material.scope === 'platform'
        || Number(material.departmentId) === 0
      );
    }

    if (session.storeId === 'all') {
      const ids = new Set(accessibleStores.value.map(store => store.id));
      return items.filter(material => (
        ids.has(material.storeId)
        || material.storeId === 'all'
        || material.storeId === 's0'
        || material.scope === 'platform'
        || Number(material.departmentId) === 0
      ));
    }
    const sid = activeMaterialStoreId();
    return items.filter(material => isMaterialVisibleForStore(material, sid));
  }

  function storeMaterialTotalCount() {
    const sid = libraryStorageKey();
    if (currentView.value !== 'materials' && currentView.value !== 'materials1') {
      return storeMaterials().length;
    }
    const count = platform.queryMaterialTotalCount?.[sid];
    return count != null ? count : storeMaterials().length;
  }

  function storeMaterialFolders() {
    const sid = libraryStorageKey();
    const folderMap = (currentView.value === 'materials' || currentView.value === 'materials1')
      ? platform.queryMaterialFolders
      : platform.materialFolders;
    const useQueryMaterials = currentView.value === 'materials' || currentView.value === 'materials1';
    if (useQueryMaterials) {
      if (session.storeId === 'all') {
        if (Array.isArray(folderMap['all']) && folderMap['all'].length > 0) {
          return folderMap['all'];
        }
        const allFolders = Object.values(folderMap).flat().filter(Boolean);
        const seenIds = new Set();
        return allFolders.filter((f) => {
          if (!f?.id || seenIds.has(String(f.id))) return false;
          seenIds.add(String(f.id));
          return true;
        });
      }
      if (!Array.isArray(folderMap[sid])) folderMap[sid] = [];
      return folderMap[sid];
    }
    if (Array.isArray(folderMap[sid]) && folderMap[sid].length > 0) {
      return folderMap[sid];
    }
    if (session.storeId !== 'all' && Array.isArray(folderMap['s1']) && folderMap['s1'].length > 0) {
      return folderMap['s1'];
    }
    if (!Array.isArray(folderMap[sid])) {
      folderMap[sid] = [];
    }
    return folderMap[sid];
  }

  async function createMaterialFolder(rawName, targetStoreId) {
    if (!canManageMaterials()) {
      showToast('当前角色无素材库管理权限', 'warn');
      return null;
    }
    const name = String(rawName || '').trim();
    if (!name) {
      showToast('请输入文件夹名称', 'warn');
      return null;
    }
    const folders = storeMaterialFolders();
    if (folders.some(f => f.name === name)) {
      showToast('已有同名文件夹', 'warn');
      return null;
    }
    if (mustUseRemoteMaterialFolderApi()) {
      try {
        const ctx = writeStoresApiContext(targetStoreId);
        const folderId = await createMaterialFolderRemote(ctx.storeId, ctx.user, name, undefined, platform.stores, ctx);
        if (!folderId) {
          throw new Error('创建文件夹失败：服务端未返回有效 ID');
        }
        await refreshMaterialLibrary();
        const refreshedFolders = storeMaterialFolders();
        let folder = refreshedFolders.find(f => String(f.id) === String(folderId));
        if (!folder) {
          folder = {
            id: String(folderId),
            name,
            ...(isQueryMaterialView(currentView.value) ? { pendingQuerySync: true } : {}),
          };
          refreshedFolders.push(folder);
        }
        showToast(`已创建文件夹「${name}」`, 'success');
        return folder;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 创建素材文件夹失败', error);
        try {
          await refreshMaterialLibrary();
          const synchronized = storeMaterialFolders().find(folder => folder.name === name);
          if (synchronized) {
            showToast(`已同步文件夹「${name}」`, 'success');
            return synchronized;
          }
        } catch { /* 保留原始创建错误 */ }
        showApiErrorToast(error, '创建文件夹失败，请稍后重试');
        return null;
      }
    }
    const folder = { id: 'mf_' + Date.now(), name };
    folders.push(folder);
    saveStorage();
    showToast(`已创建文件夹「${name}」`, 'success');
    return folder;
  }

  async function renameMaterialFolder(folderId, rawName) {
    if (!canManageMaterials()) {
      showToast('当前角色无素材库管理权限', 'warn');
      return false;
    }
    const name = String(rawName || '').trim();
    const folders = storeMaterialFolders();
    const folder = folders.find(item => folderIdsEqual(item.id, folderId));
    if (!folder || !name) return false;
    if (folders.some(item => !folderIdsEqual(item.id, folderId) && item.name === name)) {
      showToast('已有同名文件夹', 'warn');
      return false;
    }
    if (mustUseRemoteMaterialFolderApi() && !isLocalOnlyFolderId(folderId)) {
      try {
        await renameMaterialFolderRemote(folderId, name);
        folder.name = name;
        pushEditorSync();
        showToast('文件夹已重命名', 'success');
        return true;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 重命名素材文件夹失败', error);
        showToast('重命名失败，请稍后重试', 'warn');
        return false;
      }
    }
    folder.name = name;
    saveStorage();
    showToast('文件夹已重命名', 'success');
    return true;
  }

  async function deleteMaterialFolder(folderId) {
    if (!canManageMaterials()) {
      showToast('当前角色无素材库管理权限', 'warn');
      return false;
    }
    const folders = storeMaterialFolders();
    const idx = folders.findIndex(f => folderIdsEqual(f.id, folderId));
    if (idx < 0) {
      showToast('未找到该文件夹，请刷新后重试', 'warn');
      return false;
    }
    const name = folders[idx].name;
    if (mustUseRemoteMaterialFolderApi() && !isLocalOnlyFolderId(folderId)) {
      try {
        await deleteMaterialFolderRemote(folderId);
        await refreshMaterialLibrary();
        showToast(`已删除文件夹「${name}」`, 'success');
        return true;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 删除素材文件夹失败', error);
        showToast('删除文件夹失败，请稍后重试', 'warn');
        return false;
      }
    }
    folders.splice(idx, 1);
    activeMaterialItems().forEach(m => {
      if (materialFolderIdsEqual(m.folderId, folderId)) delete m.folderId;
    });
    saveStorage();
    showToast(`已删除文件夹「${name}」`, 'success');
    return true;
  }

  function folderMaterialCount(folderId) {
    const folder = storeMaterialFolders().find(f => f.id === folderId);
    if (folder?.materialCount != null) return folder.materialCount;
    const target = normalizeMaterialFolderId(folderId);
    return storeMaterials().filter(m => normalizeMaterialFolderId(m.folderId) === target).length;
  }

  async function deleteMaterial(materialId) {
    const materialItems = activeMaterialItems();
    const idx = materialItems.findIndex(m => m.id === materialId);
    if (idx < 0) return false;
    const target = materialItems[idx];
    if (!canDeleteMaterial(target)) {
      showToast('当前角色无删除该素材权限', 'warn');
      return false;
    }
    const name = target.name;
    if (USE_REMOTE_MATERIALS) {
      try {
        await deleteMaterialRemote(materialId);
        materialItems.splice(idx, 1);
        pushEditorSync();
        showToast(`已删除素材「${name}」`, 'success');
        return true;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 远程删除素材失败', error);
        showToast('删除素材失败，请稍后重试', 'warn');
        return false;
      }
    }
    materialItems.splice(idx, 1);
    saveStorage();
    showToast(`已删除素材「${name}」`, 'success');
    return true;
  }

  async function batchDeleteMaterials(materialIds = []) {
    const ids = [...new Set(materialIds.filter(Boolean))];
    if (!ids.length) return false;
    const materialItems = activeMaterialItems();
    const deletableIds = ids.filter((id) => {
      const material = materialItems.find(item => String(item.id) === String(id));
      return canDeleteMaterial(material);
    });
    if (!deletableIds.length) {
      showToast('所选素材均无删除权限', 'warn');
      return false;
    }
    if (deletableIds.length < ids.length) {
      showToast(`已跳过 ${ids.length - deletableIds.length} 个无权限素材`, 'warn');
    }
    if (USE_REMOTE_MATERIALS) {
      try {
        await batchDeleteMaterialsRemote(deletableIds);
        const idSet = new Set(deletableIds.map(String));
        replaceActiveMaterialItems(activeMaterialItems().filter(m => !idSet.has(String(m.id))));
        pushEditorSync();
        showToast(`已删除 ${deletableIds.length} 个素材`, 'success');
        return true;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 批量删除素材失败', error);
        showToast('批量删除失败，请稍后重试', 'warn');
        return false;
      }
    }
    deletableIds.forEach((id) => {
      const idx = materialItems.findIndex(m => m.id === id);
      if (idx >= 0) materialItems.splice(idx, 1);
    });
    saveStorage();
    showToast(`已删除 ${deletableIds.length} 个素材`, 'success');
    return true;
  }

  async function moveMaterialToFolder(materialId, folderId) {
    if (!canManageMaterials()) {
      showToast('当前角色无素材库管理权限', 'warn');
      return false;
    }
    const m = activeMaterialItems().find(x => String(x.id) === String(materialId));
    if (!m) return false;
    if (folderId) {
      const folders = storeMaterialFolders();
      if (!folders.some(f => String(f.id) === String(folderId))) return false;
    }
    if (mustUseRemoteMaterialFolderApi()) {
      try {
        await moveMaterialToFolderRemote(materialId, folderId);
        if (folderId) m.folderId = normalizeMaterialFolderId(folderId);
        else delete m.folderId;
        try {
          await refreshMaterialLibrary();
        } catch { /* 忽略刷新异常，本地已更新 */ }
        pushEditorSync();
        return true;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 移动素材失败', error);
        showToast('移动素材失败，请稍后重试', 'warn');
        return false;
      }
    }
    if (folderId) m.folderId = normalizeMaterialFolderId(folderId);
    else delete m.folderId;
    saveStorage();
    return true;
  }

  async function batchMoveMaterials(materialIds = [], folderId) {
    const ids = [...new Set(materialIds.filter(Boolean))];
    if (!ids.length) return false;
    if (!canManageMaterials()) {
      showToast('当前角色无素材库管理权限', 'warn');
      return false;
    }
    if (folderId) {
      const folders = storeMaterialFolders();
      if (!folders.some(f => String(f.id) === String(folderId))) return false;
    }
    if (mustUseRemoteMaterialFolderApi()) {
      try {
        await batchMoveMaterialsRemote(folderId, ids);
        const materialItems = activeMaterialItems();
        ids.forEach((id) => {
          const material = materialItems.find(item => String(item.id) === String(id));
          if (!material) return;
          if (folderId) material.folderId = normalizeMaterialFolderId(folderId);
          else delete material.folderId;
        });
        try {
          await refreshMaterialLibrary();
        } catch { /* 忽略刷新异常，本地已更新 */ }
        pushEditorSync();
        return true;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 批量移动素材失败', error);
        showToast('批量移动失败，请稍后重试', 'warn');
        return false;
      }
    }
    const materialItems = activeMaterialItems();
    ids.forEach((id) => {
      const material = materialItems.find(item => String(item.id) === String(id));
      if (!material) return;
      if (folderId) material.folderId = normalizeMaterialFolderId(folderId);
      else delete material.folderId;
    });
    saveStorage();
    return true;
  }

  async function renameMaterial(materialId, rawName) {
    if (!canManageMaterials()) {
      showToast('当前角色无素材库管理权限', 'warn');
      return false;
    }
    const materialItems = activeMaterialItems();
    const material = materialItems.find(item => item.id === materialId);
    const name = String(rawName || '').trim();
    if (!material || !name) return false;
    const duplicate = materialItems.some(item =>
      item.id !== materialId
      && item.storeId === material.storeId
      && item.folderId === material.folderId
      && item.name === name
    );
    if (duplicate) {
      showToast('已有同名素材，请换一个名称', 'warn');
      return false;
    }
    if (USE_REMOTE_MATERIALS) {
      try {
        await renameMaterialRemote(materialId, name);
        material.name = name;
        pushEditorSync();
        showToast('素材已重命名', 'success');
        return true;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 重命名素材失败', error);
        showToast('重命名失败，请稍后重试', 'warn');
        return false;
      }
    }
    material.name = name;
    saveStorage();
    showToast('素材已重命名', 'success');
    return true;
  }

  async function uploadMaterial(file, options = {}, maybeCategory = 'upload', maybeFolderId = null) {
    if (!file) return null;
    if (!perms.value.uploadMaterials) {
      showToast('当前角色无上传素材权限', 'warn');
      return null;
    }
    if (!/^image\//.test(file.type)) {
      showToast('请上传图片素材', 'warn');
      return null;
    }
    if (file.size > 8 * 1024 * 1024) {
      showToast('单个素材不能超过 8 MB', 'warn');
      return null;
    }
    const opts = typeof options === 'string'
      ? { name: options, category: maybeCategory, folderId: maybeFolderId }
      : (options || {});
    const name = String(opts.name || opts.materialName || '').trim() || file.name.replace(/\.\w+$/, '') || '素材';
    if (name.length > 60) {
      showToast('素材名称不能超过 60 个字', 'warn');
      return null;
    }
    if (materialsAtUploadLocation(opts.folderId).some((item) => item.name === name)) {
      return { error: '当前位置已有同名素材，请修改名称' };
    }
    try {
      const fileMeta = await readImageFileMeta(file);
      const rawCategory = opts.materialType || opts.category || maybeCategory || 'upload';
      const category = rawCategory === 'qr' ? 'qrcode' : rawCategory;
      if (USE_REMOTE_MATERIALS) {
        const ctx = storesApiContext();
        const materialId = await uploadMaterialRemote(ctx.storeId, ctx.user, file, {
          name,
          category,
          materialType: category,
          folderId: opts.folderId,
          width: fileMeta.width,
          height: fileMeta.height,
          fileExt: fileMeta.fileExt,
          fileSize: file.size,
        }, platform.stores, ctx);
        await refreshMaterialLibrary();
        const materialItems = activeMaterialItems();
        const material = materialItems.find(m => String(m.id) === String(materialId))
          || materialItems.find(m => m.name === name && (
            session.storeId === 'all'
              ? isPlatformScopeMaterial(m)
              : m.storeId === activeMaterialStoreId()
          ));
        showToast('素材已上传', 'success');
        return material || { id: String(materialId), name, storeId: activeMaterialStoreId(), category, url: material?.url };
      }
      const ownerId = resolveUserId(user.value);
      const material = {
        id: 'm_' + Date.now(),
        storeId: activeMaterialStoreId(),
        name,
        category,
        url: fileMeta.originUrl,
        thumb: fileMeta.thumbUrl,
        uploadedAt: new Date().toLocaleDateString('zh-CN'),
        uploadUserId: ownerId,
        createUserId: ownerId,
        createUserName: user.value?.name || session.userName || '',
      };
      if (options.folderId) material.folderId = options.folderId;
      activeMaterialItems().unshift(material);
      saveStorage();
      showToast('素材已上传', 'success');
      return material;
    } catch (err) {
      showToast('处理素材失败', 'warn');
      throw err;
    }
  }

  function paletteScopeInfo() {
    const onAllPlatform = isAllPlatformStore(session.storeId);
    const isGlobal = roleKey.value === 'platform_admin' || onAllPlatform;
    const storeKey = resolvePaletteStoreKey(isGlobal, session.storeId);
    return { isGlobal, storeKey, scope: isGlobal ? 'global' : 'store' };
  }

  function paletteColorList() {
    const { isGlobal } = paletteScopeInfo();
    if (isGlobal) return platform.commonColors.global || [];
    const sid = session.storeId === 'all' ? 's1' : session.storeId;
    if (!platform.commonColors.stores[sid]) {
      platform.commonColors.stores[sid] = USE_ART_API
        ? []
        : (platform.commonColors.global || []).map(c => ({ ...c, id: `${c.id}_${sid}` }));
    }
    return platform.commonColors.stores[sid];
  }

  async function refreshPaletteLibrary() {
    const { storeId, user } = getMaterialApiContext();
    await syncPaletteLibrary(platform, storeId, user);
  }

  async function savePaletteColor(colorInput, editingId = null) {
    const { isGlobal, storeKey, scope } = paletteScopeInfo();
    const list = paletteColorList();
    const name = String(colorInput.name || '').trim() || '新色板';
    if (list.some(item => item.id !== editingId && item.name.trim() === name)) {
      showToast('已有同名色板，请换一个名称', 'warn');
      return false;
    }
    const next = {
      id: editingId || `c_${Date.now()}`,
      name,
      c: Number(colorInput.c ?? 0),
      m: Number(colorInput.m ?? 0),
      y: Number(colorInput.y ?? 0),
      k: Number(colorInput.k ?? 0),
    };
    if (USE_REMOTE_PALETTES) {
      try {
        const { user } = getMaterialApiContext();
        const apiContext = storesApiContext();
        const meta = platform.commonColors._meta || {};
        const sid = resolveWriteStoreId();
        const paletteId = isGlobal
          ? meta.globalPaletteId
          : meta.storePaletteIds?.[sid];
        const existing = editingId ? list.find(item => item.id === editingId) : null;
        const saved = await savePaletteColorRemote(storeKey, user, scope, {
          ...next,
          id: editingId || undefined,
          paletteId: existing?.paletteId,
          sort: existing?.sort ?? list.length,
        }, paletteId, platform.stores, apiContext);
        await fetchPaletteScope(platform, storeKey, user, scope, apiContext);
        showToast(`已${editingId ? '更新' : '添加'}色板「${saved.name}」`, 'success');
        return true;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 保存色板失败', error);
        showToast(error?.message || '保存色板失败，请稍后重试', 'warn');
        return false;
      }
    }
    const index = list.findIndex(item => item.id === editingId);
    if (index >= 0) list[index] = next;
    else list.push(next);
    saveStorage();
    showToast(`已${index >= 0 ? '更新' : '添加'}色板「${name}」`, 'success');
    return true;
  }

  async function removePaletteColor(colorId) {
    const list = paletteColorList();
    const index = list.findIndex(item => item.id === colorId);
    if (index < 0) return false;
    const name = list[index].name;
    if (USE_REMOTE_PALETTES) {
      try {
        const { user } = getMaterialApiContext();
        const { storeKey } = paletteScopeInfo();
        await deletePaletteColorRemote(colorId, storeKey, user);
        list.splice(index, 1);
        showToast(`已删除色板「${name}」`, 'success');
        return true;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 删除色板失败', error);
        showToast(error?.message || '删除色板失败，请稍后重试', 'warn');
        return false;
      }
    }
    list.splice(index, 1);
    saveStorage();
    showToast(`已删除色板「${name}」`, 'success');
    return true;
  }

  function storeTasks() {
    return platform.designTasks.filter(t => user.value.stores?.includes(t.storeId));
  }

  function standaloneDesigns() {
    const stores = user.value.stores || [];
    const sid = session.storeId === 'all' ? null : session.storeId;
    const account = session.role;
    const accountName = user.value.name;
    return platform.standaloneDesigns.filter(d => {
      if (stores.length > 0 && !stores.includes(d.storeId)) return false;
      if (sid && d.storeId !== sid) return false;
      if (!belongsToCurrentAccount(d, account, roleKey.value, accountName)) return false;
      return true;
    });
  }

  async function useTemplate(tpl, options = {}) {
    if (!tpl) return;
    const initialTpl = tpl;
    const editorId = 'd_' + Date.now();
    const editMaster = options.editMaster === true;

    // 1. 立即无缝打开编辑器并跳转路由（0ms 响应，即刻展示 WhaleLoading 加载动效）
    openEditor({
      id: editorId,
      storeId: session.storeId === 'all' ? initialTpl.storeId : session.storeId,
      templateId: initialTpl.id,
      templateName: initialTpl.name,
      thumb: initialTpl.thumb,
      layerJson: initialTpl.layerJson,
      originFileUrl: initialTpl.originFileUrl,
      folderId: initialTpl.folderId,
      orderId: '',
      songName: '',
      version: '1.0',
      ownerRole: user.value.role,
      ownerName: user.value.name,
      isNew: !editMaster,
      fromTemplate: initialTpl.id,
      fromTemplateLibrary: true,
      standalone: true,
      isCopy: !editMaster && initialTpl.type === 'master',
      editMasterTemplate: editMaster,
      pendingMasterEdit: Boolean(editMaster && initialTpl.pendingMasterEdit),
      printConfig: getPrintConfig(platform, session.storeId),
    });

    // 2. 若为远程模版且本地未缓存分层数据，在后台并发恢复/拉取分层
    if (USE_REMOTE_TEMPLATES && initialTpl?.id && !String(initialTpl.id).startsWith('t_') && !initialTpl.layerJson) {
      try {
        const { storeId, user: materialUser } = getMaterialApiContext();
        const working = await resolveRemoteTemplateForUse({
          storeId,
          user: materialUser,
          templateId: initialTpl.id,
          existing: initialTpl,
          stores: platform.stores,
          departmentId: session.departmentId,
          departmentName: session.departmentName,
          recoverFromPdf: true,
          onRecoverStart: () => {
            templateImportState.value = {
              active: true,
              message: '云端模版缺少分层数据，正在从原 PDF 自动恢复…',
            };
          },
          onRecoverSuccess: () => {
            templateImportState.value = { active: false, message: '' };
            showToast('模版分层已恢复，已缓存到本机', 'success');
          },
          persistRecovered: savePendingTemplate,
        });
        platform.templates = platform.templates.filter(item => (
          String(item.id) !== String(working.id) && String(item.id) !== String(initialTpl.id)
        ));
        platform.templates.unshift(working);
        const matchesCurrentEditor = editorMeta.value && (
          String(editorMeta.value.templateId || '') === String(working.id)
          || String(editorMeta.value.fromTemplate || '') === String(working.id)
          || String(editorMeta.value.templateId || '') === String(initialTpl.id)
          || String(editorMeta.value.fromTemplate || '') === String(initialTpl.id)
        );
        if (matchesCurrentEditor) {
          editorMeta.value.layerJson = working.layerJson;
          editorMeta.value.thumb = working.thumb || editorMeta.value.thumb;
          editorMeta.value.templateName = working.name || editorMeta.value.templateName;
          persistActiveEditorMeta(editorMeta.value);
        }
        pushEditorSync();
      } catch (error) {
        templateImportState.value = { active: false, message: '' };
        if (import.meta.env.DEV) console.warn('[art-api] 模版分层解析失败', error);
      }
    }
  }

  async function editMasterTemplate(tpl) {
    if (!tpl) return;
    return useTemplate(tpl, { editMaster: true });
  }

  async function refreshActiveEditorTemplate(options = {}) {
    const meta = editorMeta.value;
    const templateId = meta?.templateId || meta?.fromTemplate;
    const force = options?.force === true;
    if ((!USE_REMOTE_TEMPLATES && !force) || !templateId) {
      return null;
    }
    try {
      const { storeId, user: materialUser } = getMaterialApiContext();
      const targetStoreId = meta.storeId && meta.storeId !== 'all' ? meta.storeId : storeId;
      const departmentId = meta.departmentId ?? (session.departmentId || null);
      const departmentName = meta.departmentName ?? (session.departmentName !== '全平台' ? session.departmentName : '');
      const existing = (platform.templates || []).find(item => String(item.id) === String(templateId)) || {
        id: templateId,
        name: meta.templateName,
        thumb: meta.thumb,
        layerJson: meta.layerJson,
        storeId: targetStoreId,
      };
      const working = await resolveRemoteTemplateForUse({
        storeId: targetStoreId,
        user: materialUser,
        templateId,
        existing,
        stores: platform.stores,
        departmentId,
        departmentName,
        recoverFromPdf: true,
        persistRecovered: savePendingTemplate,
      });
      if (working?.id) {
        platform.templates = [
          working,
          ...platform.templates.filter(item => String(item.id) !== String(working.id) && String(item.id) !== String(templateId)),
        ];
        if (editorMeta.value && (
          String(editorMeta.value.templateId || '') === String(working.id)
          || String(editorMeta.value.fromTemplate || '') === String(working.id)
          || String(editorMeta.value.templateId || '') === String(templateId)
          || String(editorMeta.value.fromTemplate || '') === String(templateId)
        )) {
          editorMeta.value.layerJson = working.layerJson || editorMeta.value.layerJson;
          editorMeta.value.thumb = working.thumb || editorMeta.value.thumb;
          editorMeta.value.templateName = (working.name && working.name !== '未命名模版')
            ? working.name
            : (editorMeta.value.templateName || working.name);
          editorMeta.value.departmentId = departmentId;
          editorMeta.value.departmentName = departmentName;
          persistActiveEditorMeta(editorMeta.value);
        }
        pushEditorSync();
        return working;
      }
    } catch (error) {
      if (import.meta.env.DEV) console.warn('[usePlatformStore] 刷新当前模版数据失败', error);
    }
    return null;
  }

  function templateNameTaken(name, folderId, storeId = session.storeId, excludeId) {
    const sid = storeId === 'all' ? activeTemplateStoreId() : storeId;
    const fid = folderId || undefined;
    const trimmed = String(name || '').trim();
    if (!trimmed) return false;
    return activeTemplateItems().some(t =>
      t.storeId === sid
      && (t.folderId || undefined) === fid
      && t.name === trimmed
      && (excludeId == null || String(t.id) !== String(excludeId))
    );
  }

  async function startDesignFromTask(task) {
    if (!task) return;
    const isFreshTask = task.status === 'waiting';
    if (isFreshTask) task.status = 'designing';
    const taskSkus = normalizeTaskSkus(task);
    const activeSku = taskSkus[0] || null;
    const activeSkuId = String(activeSku?.id || 'default');
    const taskSessionKey = `task_${task.id || task.orderId}_${Date.now()}`;

    const byUpdated = (a, b) => {
      const ta = new Date(a.updated || a.savedAt || 0).getTime();
      const tb = new Date(b.updated || b.savedAt || 0).getTime();
      return tb - ta;
    };
    const existingDesign = platform.designs
      .filter(d => d.orderId === task.orderId
        && (d.state || d.layerJson)
        && !d.parentDesignId
        && (String(d.skuId || '') === activeSkuId || (taskSkus.length === 1 && !d.skuId)))
      .sort(byUpdated)[0];
    const existingDraft = drafts.value
      .filter(d => d.orderId === task.orderId
        && (d.state || d.layerJson)
        && (String(d.skuId || '') === activeSkuId || (taskSkus.length === 1 && !d.skuId)))
      .sort(byUpdated)[0];
    // “待设计”表示一次全新的设计入口，不能被同订单号的历史脏草稿劫持。
    const existing = isFreshTask ? null : (existingDesign || existingDraft);

    if (existing?.state || existing?.layerJson) {
      await openEditor({
        id: existing.id,
        storeId: existing.storeId || task.storeId,
        templateName: existing.templateName || task.productCategory || '设计任务',
        designName: existing.designName || existing.songName || existing.templateName,
        thumb: existing.thumb || '',
        orderId: task.orderId,
        songName: existing.songName || platform.defaultSongName,
        version: existing.version || '1.0',
        ownerRole: existing.ownerRole || user.value.role,
        ownerName: existing.ownerName || user.value.name,
        ownerId: existing.ownerId || session.role,
        isNew: false,
        state: existing.state,
        layerJson: existing.layerJson,
        fromTaskId: task.id,
        taskSkus,
        activeSku,
        skuId: activeSkuId,
        skuName: activeSku?.name || '',
        skuConfirmed: false,
        requiresSkuConfirmation: true,
        taskSessionKey,
        ignoreHistoricalTaskDesigns: isFreshTask,
        printConfig: getPrintConfig(platform, existing.storeId || task.storeId),
      });
      showToast(existingDesign ? '已载入该 SKU 的已保存设计，请确认后继续' : '已载入该 SKU 草稿，请确认后继续', 'success');
      return;
    }

    await openEditor({
      id: 'd_' + Date.now(),
      storeId: task.storeId,
      templateName: '',
      designName: task.orderId ? `订单 ${task.orderId}` : '设计任务',
      taskCategory: task.productCategory || '',
      thumb: '',
      orderId: task.orderId,
      songName: platform.defaultSongName,
      version: '1.0',
      ...currentOwnerMeta(),
      isNew: true,
      skipAutoTemplate: true,
      requiresTemplateSelection: true,
      fromTaskId: task.id,
      taskSkus,
      activeSku,
      skuId: activeSkuId,
      skuName: activeSku?.name || '',
      skuConfirmed: false,
      requiresSkuConfirmation: true,
      taskSessionKey,
      ignoreHistoricalTaskDesigns: isFreshTask,
      printConfig: getPrintConfig(platform, task.storeId),
    });
    saveStorage();
    showToast(`订单 ${task.orderId} 已进入设计，请先确认 SKU`, 'success');
  }

  function switchTaskSkuFromEditor(sku) {
    const current = editorMeta.value;
    if (!current?.fromTaskId || !sku) return { switched: false };
    const skuId = String(sku.id || sku.skuId || '');
    if (!skuId) return { switched: false };

    const shared = {
      taskSkus: current.taskSkus || [],
      activeSku: { ...sku, id: skuId },
      skuId,
      skuName: sku.name || '',
      skuConfirmed: true,
      requiresSkuConfirmation: false,
    };
    if (String(current.skuId || '') === skuId) {
      editorMeta.value = { ...current, ...shared };
      return { switched: true, sameDesign: true, meta: editorMeta.value };
    }

    const byUpdated = (a, b) => {
      const ta = new Date(a.updated || a.savedAt || 0).getTime();
      const tb = new Date(b.updated || b.savedAt || 0).getTime();
      return tb - ta;
    };
    const candidates = [
      ...platform.designs,
      ...drafts.value,
      ...editorDraftCache.value,
    ].filter(item => item.orderId === current.orderId
      && String(item.skuId || '') === skuId
      && (item.state || item.layerJson)
      && (!current.ignoreHistoricalTaskDesigns || item.taskSessionKey === current.taskSessionKey))
      .sort(byUpdated);
    const existing = candidates[0];

    editorMeta.value = existing
      ? {
        ...current,
        ...shared,
        id: existing.id,
        storeId: existing.storeId || current.storeId,
        templateId: existing.templateId,
        fromTemplate: existing.fromTemplate || existing.templateId,
        templateName: existing.templateName || '',
        designName: existing.designName || `订单 ${current.orderId} · ${sku.name}`,
        thumb: existing.thumb || '',
        state: existing.state,
        layerJson: existing.layerJson,
        isNew: false,
        requiresTemplateSelection: false,
      }
      : {
        ...current,
        ...shared,
        id: `d_${Date.now()}_${skuId.replace(/[^\w-]/g, '').slice(0, 18) || 'sku'}`,
        templateId: undefined,
        fromTemplate: undefined,
        templateName: '',
        designName: `订单 ${current.orderId} · ${sku.name}`,
        thumb: '',
        state: undefined,
        layerJson: undefined,
        isNew: true,
        skipAutoTemplate: true,
        requiresTemplateSelection: true,
      };
    return { switched: true, sameDesign: false, restored: Boolean(existing), meta: editorMeta.value };
  }

  async function applyTemplateImportLayers(tpl, file, options = {}) {
    if (isUnitTestRuntime()) {
      tpl.layerJson = '{"objects":[]}';
      return { layerJson: tpl.layerJson };
    }
    const layers = await resolveTemplateImportLayers(file, {
      dpi: options.dpi,
      storeId: options.storeId,
      name: options.name,
      folderId: options.folderId,
    });
    if (!layers.layerJson) {
      throw new Error('未识别到模版分层数据，无法导入');
    }
    Object.assign(tpl, {
      layerJson: layers.layerJson,
      thumb: layers.thumb || tpl.thumb,
      originFileUrl: layers.originFileUrl || tpl.originFileUrl,
      pageCount: layers.pageCount || tpl.pageCount,
      importStats: layers.importStats,
      importWarnings: layers.importWarnings,
    });
    return layers;
  }

  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('读取文件失败'));
      reader.readAsDataURL(file);
    });
  }

  async function readTemplateTextAsset(asset) {
    if (!asset || typeof asset !== 'string') return '';
    const trimmed = asset.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) return trimmed;
    const response = await fetch(asset);
    if (!response.ok) throw new Error(`读取模板图层失败: ${response.status}`);
    return response.text();
  }

  async function readTemplateFileAsset(asset, fallbackName, fallbackType) {
    if (!asset || typeof asset !== 'string') return null;
    const response = await fetch(asset);
    if (!response.ok) throw new Error(`读取模板资源失败: ${response.status}`);
    const blob = await response.blob();
    return new File([blob], fallbackName, { type: blob.type || fallbackType });
  }

  async function uploadTemplateAssetToOss(asset, fallbackName, fallbackType, shopId) {
    if (/^https?:\/\//i.test(String(asset || ''))) return { url: asset };
    const file = await readTemplateFileAsset(asset, fallbackName, fallbackType);
    return file ? uploadTemplateFileToOss(file, shopId) : null;
  }

  async function preparePdfTemplateForRemote(tpl, sourceFile, storeId, roleUser) {
    const shopId = resolveShopId(storeId, roleUser);
    const layerJson = await normalizeTemplateLayerJson(tpl.layerJson);
    const coverFile = await readTemplateFileAsset(
      tpl.thumb,
      `${String(tpl.name || 'template').replace(/[^\w\u4e00-\u9fff-]+/g, '_')}.png`,
      'image/png',
    );
    const sourceAsset = tpl.originFileUrl;
    if (!sourceAsset && !sourceFile) {
      throw new Error('待同步模板缺少原始 PDF 文件');
    }
    const [sourceUpload, coverUpload] = await Promise.all([
      // 浏览器仍持有原 File 时直接上传，避免先从本地 PDF 服务重新下载数十 MB。
      sourceFile
        ? uploadTemplateFileToOss(sourceFile, shopId)
        : uploadTemplateAssetToOss(
            sourceAsset,
            tpl.sourceFileName || 'template.pdf',
            'application/pdf',
            shopId,
          ),
      /^https?:\/\//i.test(String(tpl.thumb || ''))
        ? Promise.resolve({ url: tpl.thumb })
        : (coverFile ? uploadTemplateFileToOss(coverFile, shopId) : Promise.resolve(null)),
    ]);
    return {
      ...tpl,
      originFileUrl: sourceUpload.url,
      thumb: coverUpload?.url || tpl.thumb,
      layerJson,
    };
  }

  function findTemplateById(templateId) {
    if (templateId == null || templateId === '') return null;
    const id = String(templateId);
    return (platform.queryTemplates || []).find(item => String(item.id) === id)
      || (platform.templates || []).find(item => String(item.id) === id)
      || null;
  }

  function upsertTemplateInLibraries(tpl, previousIds = []) {
    if (!tpl?.id) return;
    const ids = new Set([String(tpl.id), ...previousIds.map(id => String(id))].filter(Boolean));
    const apply = (list) => {
      const next = (list || []).filter(item => !ids.has(String(item.id)));
      next.unshift(tpl);
      return next;
    };
    platform.templates = apply(platform.templates);
    platform.queryTemplates = apply(platform.queryTemplates);
  }

  function serializeEditorLayerJson(payload = {}) {
    return toFullEditorLayerJson(payload.state ?? payload.layerJson);
  }

  function isMasterTemplateEdit(payload = {}) {
    return Boolean(payload.editMasterTemplate || editorMeta.value?.editMasterTemplate);
  }

  async function persistPdfTemplateToRemote(localTpl, sourceFile, baseName, folderId, options = {}) {
    const localTemplateId = localTpl.id;
    const persistKey = String(localTemplateId || `${baseName}:${folderId ?? ''}`);
    const existingTask = persistTemplateInFlight.get(persistKey);
    if (existingTask) return existingTask;

    const task = (async () => {
    const ctx = writeStoresApiContext();
    const remoteReadyTpl = await preparePdfTemplateForRemote(localTpl, sourceFile, ctx.storeId, ctx.user);
    const imported = await importTemplateWithLayerStorageRemote(ctx.storeId, ctx.user, {
      ...remoteReadyTpl,
      name: baseName,
      folderId,
      type: remoteReadyTpl.type || 'master',
      templateLayerId: localTpl.templateLayerId || localTpl.trackId || remoteReadyTpl.templateLayerId,
      coverThumbUrl: remoteReadyTpl.coverThumbUrl || localTpl.coverThumbUrl,
    }, platform.stores, { requireLayerJson: true, autoUseTemplate: false, ...ctx });

    const persisted = {
      ...localTpl,
      ...imported.template,
      layerJson: imported.layerJson,
      pendingPersistence: !imported.layerStorageConfirmed,
      pendingLayerSync: !imported.layerStorageConfirmed,
      pendingMasterEdit: false,
      masterConfirmed: imported.layerStorageConfirmed,
    };

    if (imported.layerStorageConfirmed) {
      removePendingTemplate(localTemplateId);
      if (!options.silent) {
        showToast(`模版「${baseName}」及分层数据已保存到云端`, 'success');
      }
    } else {
      savePendingTemplate(persisted);
      if (!options.silent) {
        showToast('模版主记录已保存，分层已提交但 page-layers 尚未返回，请稍后刷新', 'warn');
      }
    }

    upsertTemplateInLibraries(persisted, [localTemplateId]);
    pushEditorSync();
    return persisted;
    })();

    persistTemplateInFlight.set(persistKey, task);
    try {
      return await task;
    } finally {
      persistTemplateInFlight.delete(persistKey);
    }
  }

  async function persistPdfTemplateInBackground(localTpl, sourceFile, baseName, folderId) {
    try {
      return await persistPdfTemplateToRemote(localTpl, sourceFile, baseName, folderId);
    } catch (error) {
      if (import.meta.env.DEV) console.warn('[art-api] 后台登记 PDF 模版失败', error);
      const bridged = savePendingTemplate({
        ...localTpl,
        pendingPersistence: true,
      });
      if (bridged) {
        platform.templates = platform.templates.filter(item => String(item.id) !== String(localTpl.id));
        platform.templates.unshift(bridged);
        pushEditorSync();
      }
      showToast(error?.message || '模版云端保存失败，已暂存本机', 'warn');
      return bridged || null;
    }
  }

  function schedulePdfTemplatePersistence(localTpl, sourceFile, baseName, folderId) {
    const run = () => {
      void persistPdfTemplateInBackground(localTpl, sourceFile, baseName, folderId);
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 4_000 });
      return;
    }
    window.setTimeout(run, 1_200);
  }

  async function importTemplateFile(file, options = {}) {
    if (!file) return null;
    if (!perms.value.uploadTpl) {
      showToast('当前角色无导入模版权限', 'warn');
      return null;
    }
    const writeStoreId = resolveWriteStoreId();

    const baseName = String(options.name || '').trim() || file.name.replace(/\.[^.]+$/, '') || file.name;
    const folderId = normalizeTemplateFolderId(options.folderId);
    if (templateNameTaken(baseName, folderId, writeStoreId)) {
      return { error: '已有同名模版，请换一个名称' };
    }
    const today = new Date().toISOString().slice(0, 10);
    const isImage = /^image\//.test(file.type);
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

    if (isPdf) {
      try {
        await assertPdfPhysicalPagesAllowed(file);
      } catch (error) {
        const message = error?.message || 'PDF 页数校验失败';
        showToast(message, 'warn');
        return { error: message };
      }
    }

    let thumb = DEFAULT_TEMPLATE_THUMB;
    try {
      if (isImage) thumb = await readFileAsDataUrl(file);
    } catch { /* keep fallback */ }

    const tags = [];
    if (isPdf) tags.push('PDF');
    else if (isImage) {
      const ext = (file.name.split('.').pop() || 'PNG').toUpperCase();
      tags.push(['JPG', 'JPEG', 'PNG', 'WEBP', 'GIF'].includes(ext) ? (ext === 'JPEG' ? 'JPG' : ext) : 'PNG');
    } else {
      const ext = (file.name.split('.').pop() || '').toUpperCase();
      if (ext) tags.push(ext);
    }

    const tpl = {
      id: 't_import_' + Date.now(),
      storeId: writeStoreId,
      name: baseName,
      type: 'store',
      scope: 'store',
      thumb,
      tags,
      pageCount: 1,
      uploaded: today,
      useCount: 0,
      sourceFileName: file.name,
      folderId,
      pendingMasterEdit: true,
      masterConfirmed: false,
      pendingPersistence: true,
    };

    if (USE_REMOTE_TEMPLATES) {
      try {
        const ctx = writeStoresApiContext();
        const shopId = resolveShopId(ctx.storeId, ctx.user, platform.stores);

        let ossSource = null;
        if (isPdf) {
          const parseSignal = beginPdfParseSession();
          templateImportState.value = {
            active: true,
            message: '正在上传 PDF 至云端存储 (0%)…',
          };

          // 1. 并行任务 A: 客户端本地异步提取首帧高清封面，并以 JPEG 智能缩放压缩 (零阻塞，耗时与主文件上传完全重合)
          const localCoverPromise = extractPdfFirstPageDataUrl(file, 1.0, {
            format: 'image/jpeg',
            quality: 0.82,
            maxDimension: 800,
          }).catch((err) => {
            if (import.meta.env.DEV) console.warn('[template-import] 客户端提取首帧封面失败', err);
            return '';
          });

          // 2. 并行任务 B: 高性能流式直传 PDF 文件至 OSS，附带实时百分比与动态 MB/s 测速
          const ossUploadPromise = uploadTemplateFileToOss(file, shopId, {
            signal: parseSignal,
            onProgress: ({ percent, formattedSpeed }) => {
              templateImportState.value = {
                active: true,
                message: `正在上传 PDF 至云端存储 (${percent}% · ${formattedSpeed})…`,
              };
            },
          });

          const [ossResult, localCover] = await Promise.all([
            ossUploadPromise,
            localCoverPromise,
          ]);

          ossSource = ossResult;

          if (parseSignal.aborted) throw Object.assign(new Error('PDF 解析已取消'), { cancelled: true });
          const ossUrl = String(ossSource?.url ?? '').trim();
          if (!/^https?:\/\//i.test(ossUrl)) {
            throw new Error('OSS 上传失败，未返回文件地址');
          }

          const parsedPdf = await importPdfTemplateLayersFromOss({
            ossUrl,
            createUserId: resolveUserId(ctx.user),
            signal: parseSignal,
            onStatus: (message) => {
              templateImportState.value = { active: true, message };
            },
          });
          if (!parsedPdf?.layerJson) {
            throw new Error('未识别到模版分层数据，无法导入');
          }
          Object.assign(tpl, {
            layerJson: parsedPdf.layerJson,
            originFileUrl: ossUrl,
            pageCount: parsedPdf.pageTotal ?? countLayerJsonPages(parsedPdf.layerJson) ?? tpl.pageCount,
          });
          const resolvedCover = resolvePdfCoverForImport({
            clientDataUrl: localCover,
            pythonCoverThumbUrl: parsedPdf.coverThumbUrl,
          });
          if (resolvedCover.url) {
            tpl.thumb = resolvedCover.url;
            if (resolvedCover.type === 'http') tpl.coverThumbUrl = resolvedCover.url;
          }
        } else {
          templateImportState.value = {
            active: true,
            message: '正在识别模版分层…',
          };
          await applyTemplateImportLayers(tpl, file, {
            dpi: options.dpi,
            storeId: writeStoreId,
            name: baseName,
            folderId,
          });
        }

        // 如果远端分层未返回首帧封面且本地尚未提取，兜底提取一次
        if (!isUsableTemplateCover(tpl.thumb) && isPdf) {
          try {
            const clientCover = await extractPdfFirstPageDataUrl(file, 1.0, {
              format: 'image/jpeg',
              quality: 0.82,
              maxDimension: 800,
            });
            if (clientCover) {
              tpl.thumb = clientCover;
            }
          } catch (err) {
            if (import.meta.env.DEV) console.warn('[template-import] 客户端提取 PDF 首帧失败', err);
          }
        }

        if (!isPdf) {
          templateImportState.value = {
            active: true,
            message: '正在上传模版源文件至云端存储…',
          };
          ossSource = await uploadTemplateFileToOss(file, shopId);
        }

        let coverThumbUrl = '';
        if (tpl.coverThumbUrl && /^https?:\/\//i.test(String(tpl.coverThumbUrl).trim())) {
          coverThumbUrl = String(tpl.coverThumbUrl).trim();
        } else if (tpl.thumb && /^https?:\/\//i.test(String(tpl.thumb).trim())) {
          coverThumbUrl = String(tpl.thumb).trim();
        } else if (isUsableTemplateCover(tpl.thumb) && String(tpl.thumb).startsWith('data:')) {
          try {
            const isJpeg = String(tpl.thumb).startsWith('data:image/jpeg');
            const ext = isJpeg ? 'jpg' : 'png';
            const mime = isJpeg ? 'image/jpeg' : 'image/png';
            const coverFile = dataUrlToFile(
              tpl.thumb,
              `${baseName.replace(/[^\w\u4e00-\u9fff-]+/g, '_')}_thumb.${ext}`,
              mime,
            );
            const ossCover = await uploadTemplateFileToOss(coverFile, shopId);
            coverThumbUrl = ossCover?.url || '';
          } catch (err) {
            if (import.meta.env.DEV) console.warn('[template-import] 上传模版首图封面失败', err);
          }
        } else if (isImage) {
          coverThumbUrl = ossSource?.url || '';
        }

        templateImportState.value = {
          active: true,
          message: '分层识别完成，正在同步模版数据…',
        };

        const remotePayload = {
          ...tpl,
          name: baseName,
          templateName: baseName,
          folderId,
          layerJson: tpl.layerJson,
          coverThumbUrl: coverThumbUrl || undefined,
          thumb: coverThumbUrl || tpl.thumb || DEFAULT_TEMPLATE_THUMB,
          originFileUrl: ossSource?.url || tpl.originFileUrl || '',
          pageCount: tpl.pageCount,
          departmentId: tpl.departmentId,
          departmentName: tpl.departmentName,
        };

        // 轮询查询到分层数据结束后，同时调用 /api/art/template/import 导入接口
        const importedTemplate = await importTemplateRemote(
          writeStoreId,
          ctx.user,
          remotePayload,
          platform.stores,
          ctx,
        );

        Object.assign(tpl, importedTemplate, {
          thumb: coverThumbUrl || importedTemplate?.coverThumbUrl || importedTemplate?.thumb || tpl.thumb || DEFAULT_TEMPLATE_THUMB,
          coverThumbUrl: coverThumbUrl || importedTemplate?.coverThumbUrl || undefined,
          originFileUrl: ossSource?.url || importedTemplate?.originFileUrl || tpl.originFileUrl || '',
          pendingMasterEdit: true,
          masterConfirmed: false,
          pendingPersistence: false,
        });

        upsertTemplateInLibraries(tpl);
        pushEditorSync();
        refreshTemplateLibrary().catch(() => {});
        showToast(`「${baseName}」分层已识别并完成导入，正在进入母模版编辑`, 'success');
      } catch (error) {
        if (error?.cancelled || error?.name === 'PdfParseCancelledError') {
          return { error: error.message || '已取消导入' };
        }
        if (import.meta.env.DEV) console.warn('[art-api] 远程导入模版失败', error);
        const message = error?.message || '远程导入模版失败，请稍后重试';
        showToast(message, 'warn');
        return { error: message };
      } finally {
        cancelPdfParsePolling();
        templateImportState.value = { active: false, message: '' };
      }
    } else {
      try {
        templateImportState.value = {
          active: true,
          message: isPdf ? '正在进行 PDF 分层识别…' : '正在识别模版分层…',
        };
        await applyTemplateImportLayers(tpl, file, {
          dpi: options.dpi,
          storeId: writeStoreId,
          name: baseName,
          folderId,
        });
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[template-import] 分层识别失败', error);
        const message = error?.message || '模版分层识别失败';
        showToast(message, 'warn');
        return { error: message };
      } finally {
        templateImportState.value = { active: false, message: '' };
      }
      upsertTemplateInLibraries(tpl);
      saveStorage();
      showToast(`「${baseName}」已进入母模版编辑，保存后才会成为正式模版`, 'success');
    }

    openEditor({
      id: 'd_' + Date.now(),
      storeId: writeStoreId,
      templateId: tpl.id,
      templateName: tpl.name,
      thumb: tpl.thumb,
      layerJson: tpl.layerJson || '',
      originFileUrl: tpl.originFileUrl || '',
      folderId: tpl.folderId,
      orderId: '',
      songName: '',
      version: '1.0',
      ownerRole: user.value.role,
      ownerName: user.value.name,
      isNew: false,
      fromTemplate: tpl.id,
      fromTemplateLibrary: true,
      standalone: true,
      skipAutoTemplate: false,
      pendingImportName: file.name,
      editMasterTemplate: true,
      pendingMasterEdit: true,
      printConfig: getPrintConfig(platform, writeStoreId),
    });
    return tpl;
  }

  async function openDesign(design) {
    const designId = String(design?.id || '');
    if (!designId || openingDesignId.value) return false;
    openingDesignId.value = designId;
    let item = design;
    try {
      if (USE_REMOTE_DESIGN_CENTER && isRemoteDesignCenterId(design.id)) {
        const ctx = storesApiContext();
        const designStoreId = design.storeId && design.storeId !== 'all'
          ? design.storeId
          : ctx.storeId;
        const remoteItem = await getDesignCenterDetailRemote(designStoreId, ctx.user, design.id, ctx);
        const remoteLayerSource = remoteItem.state ?? remoteItem.layerJson;
        const cachedLayerSource = design.state ?? design.layerJson;
        if (!hasUsableEditorLayerState(remoteLayerSource)) {
          if (!hasUsableEditorLayerState(cachedLayerSource)) {
            throw new Error('该设计的云端分层数据不完整，请返回原设计重新保存');
          }
          item = {
            ...design,
            ...remoteItem,
            state: parseEditorLayerState(cachedLayerSource),
            layerJson: typeof cachedLayerSource === 'string'
              ? cachedLayerSource
              : JSON.stringify(cachedLayerSource),
          };
        } else {
          item = { ...design, ...remoteItem };
        }
      }
      const meta = {
        id: item.id,
        storeId: item.storeId,
        templateName: item.templateName,
        templateId: item.templateId || item.fromTemplate,
        fromTemplate: item.fromTemplate || item.templateId,
        thumb: item.thumb,
        orderId: item.orderId || '',
        songName: item.songName,
        version: item.version,
        ownerRole: item.ownerRole,
        ownerName: item.ownerName,
        standalone: item.standalone ?? design.standalone,
        designName: item.designName,
        state: item.state,
        layerJson: item.layerJson,
        isNew: false,
        printConfig: getPrintConfig(platform, item.storeId),
      };
      if (roleKey.value === 'customer_service' && item.ownerRole === 'designer' && item.status === 'done' && perms.value.csBranchEdit) {
        showToast('客服改稿：已打开设计师稿件（演示）', 'success');
      }
      await openEditor(meta);
      return true;
    } catch (error) {
      if (import.meta.env.DEV) console.warn('[art-api] 加载设计详情失败', error);
      showToast(error?.message || '加载设计详情失败', 'warn');
      return false;
    } finally {
      openingDesignId.value = '';
    }
  }

  async function openNewStandalone() {
    const storeId = resolveWriteStoreId(session.storeId);
    const store = resolveStoreRecord(storeId, platform.stores);
    const departmentId = store?.departmentId ?? (session.departmentId || null);
    const departmentName = store?.name || store?.departmentName || (session.departmentName !== '全平台' ? session.departmentName : '');
    await openEditor({
      storeId,
      departmentId,
      departmentName,
      templateName: '未命名设计',
      designName: '未命名设计',
      mode: 'studio',
      standalone: true,
      isNew: true,
      id: 'sd_' + Date.now(),
      ...currentOwnerMeta(),
      printConfig: getPrintConfig(platform, storeId),
    });
  }

  async function openEditor(meta) {
    saveStorage();
    const router = getAppRouter();
    const currentRoute = router?.currentRoute?.value;
    const currentTab = currentView.value || currentRoute?.params?.tab || 'templates';
    const activeFolder = meta?.folderId || selectedTemplateFolderId.value;
    if (activeFolder) {
      selectedTemplateFolderId.value = activeFolder;
    }
    let recordedRoute = (currentRoute?.path?.startsWith('/platform') ? currentRoute.fullPath : '')
      || getEditorReturnRoute()
      || `/platform/${currentTab}`;
    if ((currentTab === 'templates' || meta?.returnView === 'templates') && selectedTemplateFolderId.value && !recordedRoute.includes('folderId=')) {
      const sep = recordedRoute.includes('?') ? '&' : '?';
      recordedRoute = `${recordedRoute}${sep}folderId=${encodeURIComponent(selectedTemplateFolderId.value)}`;
    }
    const returnView = meta?.returnView || currentTab;

    editorReturnView.value = returnView;
    setEditorReturnRoute(recordedRoute);

    const isFromTpl = Boolean(
      meta?.fromTemplateLibrary
      || meta?.fromTemplate
      || isQueryTemplateView(currentView.value)
      || isQueryTemplateView(editorReturnView.value)
      || currentView.value === 'templates'
      || currentView.value === 'templates1'
      || editorReturnView.value === 'templates'
      || editorReturnView.value === 'templates1'
    );
    const targetStoreId = meta?.storeId && meta.storeId !== 'all' ? meta.storeId : resolveWriteStoreId(session.storeId);
    const storeRec = resolveStoreRecord(targetStoreId, platform.stores);
    const departmentId = meta?.departmentId ?? storeRec?.departmentId ?? (session.departmentId || null);
    const departmentName = meta?.departmentName ?? storeRec?.name ?? storeRec?.departmentName ?? (session.departmentName !== '全平台' ? session.departmentName : '');

    const resolvedMeta = {
      ...meta,
      folderId: activeFolder || meta?.folderId,
      platformSessionStoreId: meta?.platformSessionStoreId || session.storeId,
      storeId: targetStoreId,
      departmentId,
      departmentName,
      returnView: editorReturnView.value,
      returnRoute: recordedRoute,
    };
    if (isFromTpl && !resolvedMeta.fromTaskId) {
      resolvedMeta.fromTemplateLibrary = true;
      resolvedMeta.standalone = true;
      resolvedMeta.orderId = '';
    }
    editorMeta.value = resolvedMeta;
    persistActiveEditorMeta(editorMeta.value);
    pushEditorSync();
    const editorQuery = {};
    if (resolvedMeta.templateId || resolvedMeta.fromTemplate) {
      editorQuery.tpl = resolvedMeta.templateId || resolvedMeta.fromTemplate;
      if (resolvedMeta.departmentId) editorQuery.dept = resolvedMeta.departmentId;
      if (resolvedMeta.departmentName) editorQuery.deptName = resolvedMeta.departmentName;
      if (resolvedMeta.templateName) editorQuery.title = resolvedMeta.templateName;
      if (resolvedMeta.editMasterTemplate) editorQuery.editMaster = '1';
    }
    if (resolvedMeta.folderId) {
      editorQuery.folderId = resolvedMeta.folderId;
    }
    const qStr = new URLSearchParams(editorQuery).toString();
    pushRoute(qStr ? `/editor?${qStr}` : '/editor');
    if (!isUnitTestRuntime()) {
      void Promise.all([
        loadOrganizationStores().catch(() => {}),
        USE_REMOTE_MATERIALS
          ? artApiSync.loadQueryMaterials({ force: true }).catch(() => {})
          : Promise.resolve(),
        USE_REMOTE_TEMPLATES
          ? (
            (isQueryTemplateView(currentView.value) || isQueryTemplateView(editorReturnView.value))
              ? artApiSync.loadQueryTemplates({ force: true }).catch(() => {})
              : artApiSync.loadTemplates({ force: true }).catch(() => {})
          )
          : Promise.resolve(),
        ensureFontsLoaded().catch(() => {}),
      ]).then(() => {
        pushEditorSync();
      });
    }
  }

  function closeEditor(returnView) {
    const prevSessionStoreId = editorMeta.value?.platformSessionStoreId;
    const originFolderId = editorMeta.value?.folderId || selectedTemplateFolderId.value;
    if (originFolderId) {
      selectedTemplateFolderId.value = originFolderId;
    }
    loadStorage();
    mergeLocalSavedDesignsIntoDrafts();
    mergeLocalDraftsIntoDrafts();

    if (prevSessionStoreId) {
      session.storeId = resolveAllowedStoreId(prevSessionStoreId);
      applySessionDepartment(session, session.storeId, platform.stores, localStorage);
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('cdp_store', session.storeId);
      }
    }

    let targetRoute = '';
    let targetView = '';

    const savedRoute = editorMeta.value?.returnRoute || getEditorReturnRoute();
    if (savedRoute && savedRoute.startsWith('/platform')) {
      targetRoute = savedRoute;
      const match = savedRoute.match(/\/platform\/([^/?#]+)/);
      targetView = match ? match[1] : (returnView || editorReturnView.value || 'templates');
    } else {
      targetView = returnView || editorMeta.value?.returnView || editorReturnView.value || 'templates';
      const folderQuery = (targetView === 'templates' && selectedTemplateFolderId.value)
        ? `?folderId=${encodeURIComponent(selectedTemplateFolderId.value)}`
        : '';
      targetRoute = `/platform/${targetView}${folderQuery}`;
    }

    editorReturnView.value = targetView;
    editorMeta.value = null;
    persistActiveEditorMeta(null);
    clearEditorReturnRoute();

    currentView.value = targetView;
    pushRoute(targetRoute);
    if (!isUnitTestRuntime()) {
      artApiSync.loadForView(targetView, { force: true }).catch(() => {});
    }
  }

  function refreshDesignCenterTab(tab, options = {}) {
    if (!USE_REMOTE_DESIGN_CENTER) return Promise.resolve();
    const opType = tab === 'drafts'
      ? DESIGN_CENTER_LIST_OP_TYPE.DRAFT
      : DESIGN_CENTER_LIST_OP_TYPE.SAVED;
    const query = String(options.searchQuery || '').trim();
    const searchParams = buildDesignCenterSearchParams(query);
    if (!query) {
      platform.designCenterSearchTotals.saved = null;
      platform.designCenterSearchTotals.drafts = null;
    }
    return artApiSync.loadDrafts({
      force: true,
      opType,
      pageNum: options.pageNum ?? 1,
      pageSize: options.pageSize ?? (query ? 10 : 200),
      searchTotalKey: tab,
      ...searchParams,
    });
  }

  function reloadFromStorage() {
    loadStorage();
    queueDesignCenterStorageSync();
  }

  async function savePrintConfig(cfg) {
    const storeId = session.storeId;
    if (!storeId || storeId === 'all') return;
    if (!platform.printConfig.stores[storeId]) platform.printConfig.stores[storeId] = {};
    Object.assign(platform.printConfig.stores[storeId], cfg);
    // 平台管理员改的是全局印刷参数；店长侧仍写本地店铺覆盖
    if (USE_REMOTE_CONFIG && roleKey.value === 'platform_admin') {
      try {
        await savePrintConfigRemote(cfg);
        Object.assign(platform.printConfig.global, cfg);
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 保存系统配置失败', error);
        showToast(error?.message || '远程保存印刷参数失败', 'warn');
        return;
      }
    }
    saveStorage();
    showToast('印刷参数已保存', 'success');
  }

  function upsertRemoteDraft(entry) {
    const id = String(entry.id);
    const idx = drafts.value.findIndex(d => String(d.id) === id);
    const next = { ...entry, id, status: entry.status || 'draft', templateType: DESIGN_CENTER_TEMPLATE_TYPE.DRAFT };
    if (idx >= 0) drafts.value[idx] = { ...drafts.value[idx], ...next };
    else drafts.value = [next, ...drafts.value];
  }

  /** 按当前画布预览图重新上传封面，禁止复用旧 coverThumbUrl */
  async function uploadFreshMasterCoverUrl(previewDataUrl, shopId, baseName) {
    const value = String(previewDataUrl || '').trim();
    if (!value.startsWith('data:')) {
      throw new Error('缺少当前画布预览图，无法保存母模版');
    }
    const mime = value.match(/^data:([^;,]+)/i)?.[1] || 'image/jpeg';
    const extension = mime === 'image/png' ? 'png' : (mime === 'image/webp' ? 'webp' : 'jpg');
    const safeName = String(baseName || 'template').replace(/[^\w\u4e00-\u9fff-]+/g, '_');
    const coverFile = dataUrlToFile(
      value,
      `${safeName}_thumb_${Date.now()}.${extension}`,
      mime,
    );
    const ossCover = await uploadTemplateFileToOss(coverFile, shopId);
    const url = String(ossCover?.url || '').trim();
    if (!/^https?:\/\//i.test(url)) {
      throw new Error('预览图上传失败，未获取到封面地址');
    }
    return url;
  }

  async function saveMasterTemplateFromEditor(payload = {}) {
    const meta = editorMeta.value || {};
    const templateId = payload.templateId || payload.fromTemplate || meta.templateId || meta.fromTemplate;
    const layerJson = serializeEditorLayerJson(payload);
    if (!layerJson) {
      showToast('当前画布没有可保存的分层内容', 'warn');
      return false;
    }

    const existing = findTemplateById(templateId) || {};
    const baseName = String(payload.templateName || meta.templateName || existing.name || '').trim();
    if (!baseName) {
      showToast('请输入母模版名称', 'warn');
      return false;
    }
    const saveKey = String(templateId || existing.id || baseName);
    const existingSave = saveMasterTemplateInFlight.get(saveKey);
    if (existingSave) return existingSave;

    const runSave = (async () => {
    const nextTpl = {
      ...existing,
      id: existing.id || templateId || `t_import_${Date.now()}`,
      storeId: existing.storeId || payload.storeId || meta.storeId,
      name: baseName,
      templateName: baseName,
      type: 'master',
      scope: 'master',
      folderId: existing.folderId ?? payload.folderId ?? meta.folderId,
      originFileUrl: existing.originFileUrl || meta.originFileUrl || payload.originFileUrl || '',
      coverThumbUrl: '',
      thumb: payload.previewDataUrl || '',
      layerJson,
      pendingMasterEdit: false,
      masterConfirmed: true,
      pendingPersistence: false,
    };
    if (!USE_REMOTE_TEMPLATES && payload.previewDataUrl) {
      nextTpl.thumb = payload.previewDataUrl;
      nextTpl.coverThumbUrl = payload.previewDataUrl;
    }
    if (templateNameTaken(baseName, nextTpl.folderId, nextTpl.storeId, nextTpl.id)) {
      showToast('当前位置已有同名模版，请换一个名称', 'warn');
      return false;
    }
    const previousId = nextTpl.id;

    try {
      if (USE_REMOTE_TEMPLATES) {
        const previewDataUrl = String(payload.previewDataUrl || '').trim();
        if (!previewDataUrl.startsWith('data:')) {
          showToast('请等待预览图生成后再保存母模版', 'warn');
          return false;
        }
        const ctx = writeStoresApiContext(nextTpl.storeId);
        const shopId = resolveShopId(ctx.storeId, ctx.user, platform.stores);
        const coverUrl = await uploadFreshMasterCoverUrl(previewDataUrl, shopId, baseName);
        nextTpl.thumb = coverUrl;
        nextTpl.coverThumbUrl = coverUrl;

        if (isRemoteTemplateId(nextTpl.id)) {
          await saveMasterTemplateRemote(ctx.storeId, ctx.user, {
            templateId: nextTpl.id,
            templateName: baseName,
            designName: baseName,
            layerJson,
            coverThumbUrl: coverUrl,
            originFileUrl: nextTpl.originFileUrl,
            opType: DESIGN_CENTER_OP_TYPE.UPDATE_TEMPLATE,
          }, platform.stores, ctx);
          try {
            const updated = await updateTemplateContentRemote(nextTpl.id, nextTpl);
            Object.assign(nextTpl, updated);
          } catch (error) {
            if (import.meta.env.DEV) console.warn('[art-api] 母模版已保存，模版库回写失败', error);
          }
          Object.assign(nextTpl, {
            coverThumbUrl: coverUrl,
            thumb: coverUrl,
            layerJson,
            pendingMasterEdit: false,
            masterConfirmed: true,
            pendingPersistence: false,
          });
        } else {
          const persisted = await persistPdfTemplateToRemote(
            nextTpl,
            null,
            baseName,
            nextTpl.folderId,
            { silent: true },
          );
          Object.assign(nextTpl, persisted, {
            coverThumbUrl: coverUrl,
            thumb: coverUrl,
            layerJson,
            pendingMasterEdit: false,
            masterConfirmed: Boolean(!persisted.pendingPersistence),
            pendingPersistence: Boolean(persisted.pendingPersistence),
          });
        }
        removePendingTemplate(previousId);
        removePendingTemplate(nextTpl.id);
      }

      upsertTemplateInLibraries(nextTpl, [previousId]);
      if (!USE_REMOTE_TEMPLATES) saveStorage();

      if (editorMeta.value) {
        editorMeta.value = {
          ...editorMeta.value,
          templateId: nextTpl.id,
          fromTemplate: nextTpl.id,
          templateName: nextTpl.name,
          thumb: nextTpl.thumb,
          coverThumbUrl: nextTpl.coverThumbUrl,
          layerJson: nextTpl.layerJson,
          originFileUrl: nextTpl.originFileUrl,
          pendingMasterEdit: false,
          editMasterTemplate: true,
        };
        persistActiveEditorMeta(editorMeta.value);
      }
      pushEditorSync();
      showToast(`母模版「${nextTpl.name}」已保存并更新预览图`, 'success');
      return { saved: true, id: nextTpl.id, masterTemplate: true };
    } catch (error) {
      if (import.meta.env.DEV) console.warn('[art-api] 保存母模版失败', error);
      const bridged = savePendingTemplate({
        ...nextTpl,
        pendingMasterEdit: true,
        pendingPersistence: true,
        masterConfirmed: false,
      });
      if (bridged) upsertTemplateInLibraries(bridged, [previousId]);
      showToast(error?.message || '母模版云端保存失败，已暂存本机', 'warn');
      return { saved: true, id: (bridged || nextTpl).id, localOnly: true, masterTemplate: true };
    }
    })();

    saveMasterTemplateInFlight.set(saveKey, runSave);
    try {
      return await runSave;
    } finally {
      saveMasterTemplateInFlight.delete(saveKey);
    }
  }

  async function autosaveDesignFromEditor(payload = {}) {
    if (isMasterTemplateEdit(payload)) {
      const templateId = payload.templateId || payload.fromTemplate || editorMeta.value?.templateId || editorMeta.value?.fromTemplate;
      const existing = findTemplateById(templateId);
      const layerJson = serializeEditorLayerJson(payload);
      if (existing && layerJson) {
        const next = {
          ...existing,
          layerJson,
          pendingMasterEdit: existing.pendingMasterEdit !== false,
          pendingPersistence: existing.pendingPersistence !== false,
        };
        upsertTemplateInLibraries(next);
        if (next.pendingPersistence || next.pendingMasterEdit) {
          savePendingTemplate(next);
        }
      }
      return { saved: true, localOnly: true, masterTemplate: true };
    }

    if (!USE_REMOTE_DESIGN_CENTER) {
      return { saved: false, localOnly: true };
    }

    const targetStoreId = resolveWriteStoreId(payload.storeId || session.storeId);
    const apiContext = writeStoresApiContext(targetStoreId);
    const user = apiContext.user;
    const layerSource = payload.state ?? payload.layerJson;
    const layerJson = serializeEditorLayerJson(payload);
    const remoteId = isRemoteDesignCenterId(payload.id) ? String(payload.id) : null;
    const entry = {
      ...payload,
      storeId: targetStoreId,
      designName: payload.designName || payload.templateName || '未命名设计',
      templateName: payload.templateName || payload.designName || '未命名设计',
      layerJson,
      templateType: DESIGN_CENTER_TEMPLATE_TYPE.DRAFT,
      opType: DESIGN_CENTER_OP_TYPE.DRAFT,
      status: 'draft',
      updated: new Date().toLocaleString('zh-CN', { hour12: false }),
    };

    try {
      if (remoteId) {
        const updated = await updateDesignCenterRemote(targetStoreId, user, {
          ...entry,
          id: remoteId,
        }, platform.stores, { silentError: true, ...apiContext });
        upsertRemoteDraft({
          ...updated,
          ...entry,
          id: remoteId,
          state: layerSource,
        });
        if (editorMeta.value && String(editorMeta.value.id) === String(payload.id)) {
          editorMeta.value = { ...editorMeta.value, id: remoteId };
          persistActiveEditorMeta(editorMeta.value);
        }
        return { saved: true, id: remoteId, updatedAt: updated.updated || entry.updated };
      }

      const created = await createDesignCenterRemote(targetStoreId, user, entry, platform.stores, { silentError: true, ...apiContext });
      upsertRemoteDraft({
        ...created,
        ...entry,
        id: created.id,
        state: layerSource,
      });
      if (editorMeta.value) {
        editorMeta.value = { ...editorMeta.value, id: created.id, isNew: false };
        persistActiveEditorMeta(editorMeta.value);
      }
      return { saved: true, id: created.id, created: true, updatedAt: created.updated || entry.updated };
    } catch (error) {
      if (import.meta.env.DEV) console.warn('[art-api] 远程草稿自动保存失败，写入本地草稿', error);
      const localId = remoteId || String(payload.id || `d_${Date.now()}`);
      upsertLocalEditorDraft({
        ...entry,
        id: localId,
        state: layerSource,
        pendingRemoteSync: true,
      });
      if (editorMeta.value && String(editorMeta.value.id) === String(payload.id)) {
        editorMeta.value = { ...editorMeta.value, id: localId, isNew: false };
        persistActiveEditorMeta(editorMeta.value);
      }
      saveStorage();
      return { saved: true, id: localId, localOnly: true };
    }
  }

  async function saveDesignFromEditor(payload) {
    if (isMasterTemplateEdit(payload)) {
      return saveMasterTemplateFromEditor(payload);
    }

    const entry = {
      ...payload,
      ...currentOwnerMeta(),
      updated: new Date().toLocaleString('zh-CN', { hour12: false }),
      status: 'done',
      templateType: DESIGN_CENTER_TEMPLATE_TYPE.SAVED,
    };

    const layerJson = serializeEditorLayerJson(payload);
    if (!hasUsableEditorLayerState(layerJson || payload.state || payload.layerJson)) {
      showToast('保存失败：当前分层数据不完整，请刷新编辑器后重试', 'error');
      return false;
    }
    let remoteId = isRemoteDesignCenterId(payload.id) ? String(payload.id) : null;

    if (USE_REMOTE_DESIGN_CENTER) {
      const targetStoreId = resolveWriteStoreId(entry.storeId || session.storeId);
      const apiContext = writeStoresApiContext(targetStoreId);
      const user = apiContext.user;
      let remoteRecord = null;
      try {
        if (remoteId) {
          const updated = await updateDesignCenterRemote(targetStoreId, user, {
            ...entry,
            id: remoteId,
            templateType: DESIGN_CENTER_TEMPLATE_TYPE.SAVED,
            opType: DESIGN_CENTER_OP_TYPE.SAVE,
            state: payload.state,
            layerJson,
          }, platform.stores, apiContext);
          remoteId = String(updated.id || remoteId);
          remoteRecord = updated;
        } else {
          const created = await createDesignCenterRemote(targetStoreId, user, {
            ...entry,
            layerJson,
            state: payload.state,
            templateType: DESIGN_CENTER_TEMPLATE_TYPE.SAVED,
            opType: DESIGN_CENTER_OP_TYPE.SAVE,
          }, platform.stores, apiContext);
          remoteId = String(created.id);
          remoteRecord = created;
        }
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 保存设计中心模版失败', error);
        showToast('云端暂不可用，已保存到本地', 'warn');
      }

      if (remoteRecord) {
        const next = {
          ...entry,
          id: remoteId,
          storeId: targetStoreId,
          remoteDesignCenter: true,
          designName: entry.designName || entry.templateName || '未命名设计',
          status: 'done',
          templateType: DESIGN_CENTER_TEMPLATE_TYPE.SAVED,
          state: undefined,
          layerJson: undefined,
        };
        try {
          const draftIdx = drafts.value.findIndex(d =>
            String(d.id) === String(payload.id) || String(d.id) === remoteId
          );
          if (draftIdx >= 0) drafts.value[draftIdx] = { ...drafts.value[draftIdx], ...next };
          else drafts.value.unshift(next);
          editorDraftCache.value = editorDraftCache.value.filter(d =>
            String(d.id) !== String(payload.id) && String(d.id) !== remoteId
          );
          if (editorMeta.value && String(editorMeta.value.id) === String(payload.id)) {
            editorMeta.value = { ...editorMeta.value, id: remoteId, isNew: false };
            persistActiveEditorMeta(editorMeta.value);
          }
          saveStorage({ reportError: false });
        } catch (error) {
          if (import.meta.env.DEV) console.warn('[art-api] 云端已保存，本地列表缓存更新失败', error);
        }
        showToast('设计已保存', 'success');
        return { saved: true, id: remoteId };
      }

      const localId = String(payload.id || remoteId || `d_${Date.now()}`);
      const localEntry = {
        ...entry,
        id: localId,
        state: layerSource != null ? compactStateForStorage(layerSource) : undefined,
        pendingRemoteSync: true,
        remoteSaved: true,
      };
      const designIdx = platform.designs.findIndex(d => String(d.id) === localId);
      if (designIdx >= 0) {
        platform.designs[designIdx] = { ...platform.designs[designIdx], ...localEntry };
      } else {
        platform.designs.unshift(localEntry);
      }
      mergeLocalSavedDesignsIntoDrafts();
      editorDraftCache.value = editorDraftCache.value.filter(d => String(d.id) !== localId);
      if (!saveStorage()) {
        showToast('保存失败：本地存储空间不足', 'error');
        return false;
      }
      showToast('设计已保存到本地', 'success');
      return { saved: true, id: localId, localOnly: true };
    }

    if (!USE_REMOTE_DESIGN_CENTER && payload.standalone) {
      const idx = platform.standaloneDesigns.findIndex(d => d.id === entry.id);
      if (idx >= 0) platform.standaloneDesigns[idx] = entry;
      else platform.standaloneDesigns.unshift(entry);
    } else if (!USE_REMOTE_DESIGN_CENTER) {
      const idx = platform.designs.findIndex(d => d.id === entry.id);
      if (idx >= 0) platform.designs[idx] = { ...platform.designs[idx], ...entry };
      else platform.designs.unshift(entry);
    }

    drafts.value = drafts.value.filter(d => d.id !== entry.id);
    if (!saveStorage()) {
      showToast('保存失败：本地存储空间不足', 'error');
      return false;
    }
    showToast('设计已保存', 'success');
    return true;
  }

  async function deleteDraft(draftOrId) {
    const draft = typeof draftOrId === 'object'
      ? draftOrId
      : drafts.value.find(item => String(item.id) === String(draftOrId));
    const draftId = draft?.id ?? draftOrId;
    const before = drafts.value.length;
    if (USE_REMOTE_DESIGN_CENTER && isRemoteDesignCenterId(draftId)) {
      try {
        const { storeId, user } = getMaterialApiContext();
        const designStoreId = draft?.storeId && draft.storeId !== 'all'
          ? draft.storeId
          : storeId;
        await deleteDesignCenterRemote(designStoreId, user, draftId, platform.stores, storesApiContext());
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 远程删除设计中心草稿失败', error);
        showToast(error?.message || '删除草稿失败', 'warn');
        return false;
      }
    }
    drafts.value = drafts.value.filter(d => d.id !== draftId);
    editorDraftCache.value = editorDraftCache.value.filter(d => d.id !== draftId);
    if (drafts.value.length === before && !USE_REMOTE_DESIGN_CENTER) return false;
    if (!USE_REMOTE_DESIGN_CENTER) saveStorage();
    showToast('草稿已删除', 'success');
    return true;
  }

  async function deleteSavedDesign(design) {
    if (!design?.id) return false;
    let removed = false;

    if (USE_REMOTE_DESIGN_CENTER && isRemoteDesignCenterId(design.id)) {
      try {
        const { storeId, user } = getMaterialApiContext();
        const designStoreId = design.storeId && design.storeId !== 'all'
          ? design.storeId
          : storeId;
        await deleteDesignCenterRemote(designStoreId, user, design.id, platform.stores, storesApiContext());
        drafts.value = drafts.value.filter(d => d.id !== design.id);
        editorDraftCache.value = editorDraftCache.value.filter(d => d.id !== design.id);
        removed = true;
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[art-api] 远程删除设计失败', error);
        showToast(error?.message || '删除设计失败', 'warn');
        return false;
      }
    }

    if (!USE_REMOTE_DESIGN_CENTER && (design.standalone || (platform.standaloneDesigns || []).some(d => d.id === design.id))) {
      const before = platform.standaloneDesigns.length;
      platform.standaloneDesigns = platform.standaloneDesigns.filter(d => d.id !== design.id);
      removed = platform.standaloneDesigns.length !== before || removed;
    }
    const designIdx = platform.designs.findIndex(d => d.id === design.id);
    if (designIdx >= 0) {
      platform.designs.splice(designIdx, 1);
      removed = true;
    }
    drafts.value = drafts.value.filter(d => d.id !== design.id);
    if (!removed) return false;
    if (!USE_REMOTE_DESIGN_CENTER) saveStorage();
    showToast('设计已删除', 'success');
    return true;
  }

  function editorSyncContext() {
    const mats = (platform.queryMaterials?.length || USE_REMOTE_MATERIALS)
      ? (platform.queryMaterials?.length ? platform.queryMaterials : platform.materials)
      : platform.materials;
    const matFolders = (platform.queryMaterialFolders && Object.keys(platform.queryMaterialFolders).length)
      ? platform.queryMaterialFolders
      : (platform.materialFolders || {});
    const tpls = (platform.queryTemplates?.length || USE_REMOTE_TEMPLATES)
      ? (platform.queryTemplates?.length ? platform.queryTemplates : platform.templates)
      : platform.templates;
    const tplFolders = (platform.queryTemplateFolders && Object.keys(platform.queryTemplateFolders).length)
      ? platform.queryTemplateFolders
      : (platform.templateFolders || {});

    return {
      useRemoteMaterials: USE_REMOTE_MATERIALS,
      useRemoteTemplates: USE_REMOTE_TEMPLATES,
      useRemoteDesignCenter: USE_REMOTE_DESIGN_CENTER,
      useRemoteFonts: USE_FONT_API && fontApiAvailable.value,
      deletedDefaultMaterialIds: platform.deletedDefaultMaterialIds || [],
      materials: mats || [],
      materialFolders: matFolders || {},
      templates: tpls || [],
      templateFolders: tplFolders || {},
      stores: accessibleStores.value || platform.stores || [],
      organizationTree: platform.organizationTree || null,
      currentStoreId: session.storeId || 'all',
    };
  }

  async function handleEditorApiRequest(action, payload = {}) {
    return executeEditorApiAction(action, payload, {
      platform,
      session,
      user,
      platformFonts,
      uploadMaterial,
      checkPlatformFontMissing,
      loadFonts,
      pushEditorSync,
      showToast,
      savePendingTemplate,
      autosaveDesignFromEditor,
      saveDesignFromEditor,
      USE_REMOTE_MATERIALS,
      USE_REMOTE_TEMPLATES,
      USE_REMOTE_DESIGN_CENTER,
      USE_FONT_API,
      fontApiAvailable,
      drafts,
    });
  }

  return {
    platform,
    session,
    currentView,
    drafts,
    toast,
    editorMeta,
    roleKey,
    isPlatformAdmin,
    isBoss,
    isStoreManager,
    isDesignerLeader,
    hasAllStoresAccess,
    canImportPdfTemplate,
    user,
    perms,
    navItems,
    currentStore,
    accessibleStores,
    organizationTree,
    orgStoreError,
    printConfig,
    canViewAllStores,
    login,
    tryAutoLoginFromToken,
    loadOrganizationStores,
    logout,
    switchStore,
    navigate,
    showToast,
    storeTemplates,
    storeTemplateTotalCount,
    storeTemplateFolders,
    selectedTemplateFolderId,
    createTemplateFolder,
    renameTemplateFolder,
    deleteTemplateFolder,
    folderTemplateCount,
    moveTemplateToFolder,
    batchMoveTemplates,
    canManageTemplates,
    canDeleteTemplate,
    canRenameTemplate,
    renameTemplate,
    deleteTemplate,
    storeDesigns,
    storeMaterials,
    storeMaterialTotalCount,
    storeMaterialFolders,
    canManageMaterials,
    canDeleteAnyMaterial,
    canDeleteMaterial,
    createMaterialFolder,
    renameMaterialFolder,
    deleteMaterialFolder,
    folderMaterialCount,
    deleteMaterial,
    batchDeleteMaterials,
    renameMaterial,
    moveMaterialToFolder,
    batchMoveMaterials,
    uploadMaterial,
    paletteColorList,
    savePaletteColor,
    removePaletteColor,
    storeTasks,
    standaloneDesigns,
    allSavedDesigns,
    allDrafts,
    useTemplate,
    editMasterTemplate,
    startDesignFromTask,
    switchTaskSkuFromEditor,
    importTemplateFile,
    cancelPdfTemplateImport: cancelPdfParsePolling,
    templateImportState,
    openDesign,
    openingDesignId,
    openNewStandalone,
    openEditor,
    syncEditorMetaFromRouteQuery,
    closeEditor,
    reloadFromStorage,
    savePrintConfig,
    saveStorage,
    saveDesignFromEditor,
    deleteDraft,
    deleteSavedDesign,
    platformFonts,
    ensureFontsLoaded,
    addPlatformFonts,
    addPlatformFontFiles,
    getPlatformFontUsage,
    checkPlatformFontMissing,
    removePlatformFont,
    fontApiAvailable,
    artApiStatus: artApiSync.apiStatus,
    artModuleLoading: artApiSync.moduleLoading,
    isCurrentViewLoading,
    refreshArtApi: () => artApiSync.syncAll({ force: true }),
    refreshModuleApi: (view, options) => artApiSync.loadForView(view || currentView.value, { force: true, ...options }),
    refreshDesignCenterTab,
    useRemoteDesignCenter: USE_REMOTE_DESIGN_CENTER,
    refreshTemplateLibrary,
    refreshActiveEditorTemplate,
    editorSyncContext,
    handleEditorApiRequest,
  };
}
